// src/features/reports/HeadcountTrendReport.jsx
//
// HEADCOUNT-TREND-REPORT: People report primary view.
//
// HEADCOUNT-TREND-V2-S1 additions:
//   • KPI strip reframed: Total company · In offices · Distributed/remote ·
//     Net change MoM · Months loaded
//   • DistributedPanel: geo bar chart showing workers with no office assignment,
//     colour-coded blue/violet by whether a Ripple office exists in that cluster
//   • Data-health flags: ⚠ on contradictory office-month source/lifecycle data
//
// Renders:
//   Back button + "People" heading
//   Controls row: worker-type toggle, region/office filters, measured-only note
//   KPI strip (5 tiles): total company · in offices · distributed/remote ·
//     net change MoM · months loaded
//   DistributedPanel — geo clusters for latest snapshot month
//   Portfolio aggregate trend line SVG (pending zone for months with no snapshot)
//   Month × office matrix table with per-row sparklines, MoM delta, status tags
//
// Data source: GET /api/reports/headcount-trend (aggregate + per-office snapshots +
//   distributedRollup + divisionRollup)
// Feature flag: report-headcount-trend   Role: viewer+
//
// Deep-link: ?reportDate=YYYY-MM-DD from Import History highlights that month.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getHeadcountTrendReport } from "../../data/reportsClient.js";
import { T } from "../../app/tokens.js";
import { OverflowTable } from "../../v2/components/ListOverflow.jsx";
import { ViewScaffold } from "../../components/ViewScaffold.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KPI_TILE_ROW_HEIGHT } from "../plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { ReportShell } from "./ReportShell.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { SortControl } from "../../v2/components/SortControl.jsx";
import { ToolbarViewToggle } from "../../v2/components/ToolbarViewToggle.jsx";
import { FilterDropdown } from "../../components/FilterDropdown.jsx";
import { TableSortCaret } from "../../v2/utils/tableThStyle.jsx";
import { AlertChip } from "../../v2/components/AlertChip.jsx";
import { useReportViewState } from "../../v2/hooks/useReportViewState.js";
import { REPORT_HC_TREND_KEY } from "../../services/portfolioStatePersistence.js";
import { foldText, matchesFoldedQuery } from "../../utils/textSearch.js";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const DENSITY_COMFORTABLE = "comfortable";
const DENSITY_COMPACT      = "compact";

// Field descriptors for useReportViewState.
const HC_VIEW_STATE_FIELDS = [
  { param: "view",    valid: ["table", "chart"],                                default: "table"       },
  { param: "density", valid: ["comfortable", "compact"],                        default: "comfortable" },
  { param: "q",                                                                  default: ""            },
  { param: "measure", valid: ["all", "ft", "contractor"],                       default: "all"         },
  // Accepts the static keys "name"/"mom" AND dynamic YYYY-MM month columns.
  { param: "sort",    validFn: (v) => v === "name" || v === "mom" || /^\d{4}-\d{2}$/.test(v),  default: "name"  },
  { param: "dir",     valid: ["asc", "desc"],                                    default: "asc"         },
  { param: "viewBy",  valid: ["office", "division", "acquired", "geography"],   default: "office"      },
  { param: "region",  array: true,                                               default: []            },
];

// Sort fields for the SortControl dropdown.
// Month columns are added dynamically in the component from allMonths.
// "name" (ascending) is the default; numeric fields default to desc.
export const HC_SORT_FIELDS_BASE = [
  { value: "name", label: "Market · Office", numeric: false },
  { value: "mom",  label: "MoM delta",       numeric: true  },
];

// ─── CompactDensitySwitch ─────────────────────────────────────────────────────

function CompactDensitySwitch({ checked, onChange, testId = "hc-density-toggle" }) {
  const trackStyle = {
    position: "relative",
    width: 34,
    height: 20,
    borderRadius: 999,
    background: checked ? T.accent : T.switchOff,
    transition: "background 0.15s",
    flexShrink: 0,
    display: "inline-block",
    verticalAlign: "middle",
    border: "none",
    padding: 0,
    cursor: "pointer",
  };
  const thumbStyle = {
    position: "absolute",
    top: 2,
    left: checked ? 16 : 2,
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: T.white,
    boxShadow: "0 1px 2px rgba(16,36,58,0.3)",
    transition: "left 0.15s",
    pointerEvents: "none",
  };
  const wrapStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    fontSize: T.fsSm5,
    fontWeight: T.fwBold,
    color: checked ? T.accent : T.mid,
    cursor: "pointer",
    userSelect: "none",
    background: "none",
    border: "none",
    padding: 0,
  };
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={onChange}
      style={wrapStyle}
    >
      <span style={trackStyle} aria-hidden="true">
        <span style={thumbStyle} />
      </span>
      Compact
    </button>
  );
}

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtN(v) {
  if (v == null) return "—";
  return Number(v).toLocaleString();
}

function fmtMonthShort(yyyyMm) {
  if (!yyyyMm) return "";
  try {
    const [y, m] = yyyyMm.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-US", { month: "short" });
  } catch {
    return yyyyMm;
  }
}

function fmtMonthLabel(yyyyMm) {
  if (!yyyyMm) return "";
  try {
    const [y, m] = yyyyMm.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  } catch {
    return yyyyMm;
  }
}

function fmtSnapshotDate(snapshotDate) {
  if (!snapshotDate) return "";
  const [year, month, day] = snapshotDate.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

// ─── Rollup row validation ────────────────────────────────────────────────────
// Logs a console.warn for every row that is missing one or more required keys.
// Used by panel useMemos so schema drift surfaces immediately in the console
// instead of silently rendering a blank chart or crashing with "undefined".

function warnBadRows(componentName, rows, requiredKeys) {
  for (const row of rows) {
    const missing = requiredKeys.filter((k) => row[k] == null);
    if (missing.length > 0) {
      console.warn(
        `[HeadcountTrendReport] ${componentName}: rollup row is missing required field(s): ${missing.join(", ")}`,
        row,
      );
    }
  }
}

function fmtDelta(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (n === 0) return "±0";
  return n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString();
}

function DirectionDelta({ value, emphasize = false }) {
  return (
    <span style={{ fontSize: T.fsXs, fontWeight: emphasize ? T.fwBold : T.fwSemibold, color: T.mid }}>
      {value != null && value !== 0 && (
        <span aria-hidden="true" style={{ fontSize: T.fsXxxs, opacity: 0.6, marginRight: 3 }}>
          {value > 0 ? "▲" : "▼"}
        </span>
      )}
      {fmtDelta(value)}
    </span>
  );
}

// ─── Derived data helpers ─────────────────────────────────────────────────────

function toYYYYMM(snapshotDate) {
  return snapshotDate ? snapshotDate.slice(0, 7) : null;
}

/**
 * Build the full month range: from Jan of the earliest-snapshot year
 * through the latest snapshot month (or current month if no data).
 */
function buildAllMonths(aggregate) {
  if (!aggregate.length) return [];
  const firstDate  = aggregate[0].snapshotDate;
  const latestDate = aggregate[aggregate.length - 1].snapshotDate;
  const startYear  = parseInt(firstDate.slice(0, 4), 10);
  const endYear    = parseInt(latestDate.slice(0, 4), 10);
  const endMonth   = parseInt(latestDate.slice(5, 7), 10);
  const months = [];
  for (let y = startYear; y <= endYear; y++) {
    const mEnd = y === endYear ? endMonth : 12;
    const mStart = y === startYear ? 1 : 1;
    for (let m = mStart; m <= mEnd; m++) {
      months.push(`${y}-${String(m).padStart(2, "0")}`);
    }
  }
  return months;
}

/**
 * Status tag text for an office with a given stage.
 * Returns null for active offices.
 */
export function stageTag(stage) {
  if (!stage) return null;
  const s = stage.toLowerCase();
  if (s === "vacated") return "Vacated";
  if (s === "closed")  return "Retired";
  if (s === "relocated") return "Relocated";
  return null;
}

/**
 * True when an office is no longer operational but has no canonical
 * lifecycle boundary for the report to use. The server preserves exitDate
 * first and closeDate as its fallback; keep that precedence here as well.
 */
export function isLifecycleDateNeeded(office) {
  const stage = String(office?.stage ?? "").toLowerCase();
  return ["closed", "vacated", "relocated"].includes(stage)
    && !(office?.exitDate ?? office?.closeDate);
}

/**
 * Get the value to display for a given worker type from a snapshot or aggregate row.
 */
function getValue(row, workerType) {
  if (!row) return null;
  switch (workerType) {
    case "ft":         return row.ft  ?? null;
    case "contractor": {
      // A source row with no contractor split is not an explicit zero.
      // Preserve that distinction for the worker-type views.
      if (row.cw == null && row.ic == null) return null;
      return (row.cw ?? 0) + (row.ic ?? 0);
    }
    default:           return row.total ?? row.officeSum ?? null;
  }
}

/**
 * Pure cell-type classifier for the headcount matrix.
 *
 * Convention:
 *   "data"    — office has a real snapshot with a non-zero measure.
 *   "zero"    — office has a real snapshot with an explicit zero measure.
 *   "gap"     — no source row exists for this office/month, or the source
 *               measure is unavailable; renders as a dash.
 *
 * Explicit zero snapshots in later calendar months of the exit-to-reopen
 * interval are stale lifecycle rows and become gaps. Non-zero rows remain
 * visible with the existing lifecycle warning because they are contradictory
 * source data that owners may need to investigate.
 *
 * Exported for unit testing; internal callers use getCellInfo() which supplies
 * the snap lookup from component state. isKnownMonth remains in the
 * signature for compatibility with existing callers and tests.
 */
export function classifyCell(snap, isKnownMonth, office, monthStr, workerType) {
  void isKnownMonth;
  const exitDate = office.exitDate ?? office.closeDate;
  const needsLifecycleDate = isLifecycleDateNeeded(office);
  const exitMonth = exitDate ? exitDate.slice(0, 7) : null;
  const reopenMonth = office.reopenDate ? office.reopenDate.slice(0, 7) : null;

  // Monthly reporting keeps the exit month as the drop/source boundary. A
  // later explicit zero is stale lifecycle data, not a real zero or change.
  const isPostExitInterval = (
    exitMonth
    && monthStr > exitMonth
    && (!reopenMonth || monthStr < reopenMonth)
  );

  if (!snap) return { type: "gap", value: null };

  const value = getValue(snap, workerType);
  if (value == null) return { type: "gap", value: null };
  if (isPostExitInterval && value === 0) return { type: "gap", value: null };

  const snapshotDate = snap.snapshotDate || `${monthStr}-01`;
  let lifecycleWarning = null;
  if (value !== 0 && office.onlineDate && snapshotDate < office.onlineDate) {
    lifecycleWarning = "pre-online";
  } else if (
    value !== 0
    &&
    exitDate
    && snapshotDate >= exitDate
    && (!office.reopenDate || snapshotDate < office.reopenDate)
  ) {
    lifecycleWarning = "post-exit";
  } else if (value === 0 && needsLifecycleDate) {
    lifecycleWarning = "missing-lifecycle-date";
  } else if (
    value === 0
    && workerType !== "contractor"
    && ["operational", "active"].includes(String(office.stage ?? "").toLowerCase())
    && (!exitDate || (office.reopenDate && snapshotDate >= office.reopenDate) || snapshotDate < exitDate)
  ) {
    lifecycleWarning = "operating-zero";
  }

  return {
    type: value === 0 ? "zero" : "data",
    value,
    ...(lifecycleWarning ? { lifecycleWarning } : {}),
  };
}

function lifecycleWarningTitle(code, office) {
  if (code === "pre-online") {
    return `Stored headcount predates the office online date${office.onlineDate ? ` (${office.onlineDate})` : ""}`;
  }
  if (code === "post-exit") {
    return `Stored headcount falls on or after the office exit date${(office.exitDate ?? office.closeDate) ? ` (${office.exitDate ?? office.closeDate})` : ""}`;
  }
  if (code === "missing-lifecycle-date") {
    return "Zero headcount needs an exit or close date for this office";
  }
  if (code === "operating-zero") {
    return "Explicit zero headcount for an operating office";
  }
  return null;
}

export const DEFAULT_MATERIAL_HEADCOUNT_CHANGE_THRESHOLD = 5;

export function classifyMaterialHeadcountChange(
  previousValue,
  currentValue,
  threshold = DEFAULT_MATERIAL_HEADCOUNT_CHANGE_THRESHOLD,
) {
  if (previousValue == null || currentValue == null || previousValue === currentValue) {
    return { isMaterial: false, kind: null, delta: null };
  }
  const delta = currentValue - previousValue;
  const isMaterial = Math.abs(delta) >= threshold;
  const kind = isMaterial
    ? previousValue === 0
      ? "from-zero"
      : currentValue === 0
        ? "to-zero"
        : "absolute"
    : null;
  return {
    isMaterial,
    kind,
    delta,
  };
}

// ─── Inline sparkline SVG ─────────────────────────────────────────────────────

function Sparkline({ values }) {
  if (!values || values.length < 2) return <svg style={{ width: 78, height: 22 }} />;
  const W = 78, H = 22, pad = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const rng = max === min ? 1 : max - min;
  const step = (W - 2 * pad) / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = pad + i * step;
    const y = H - pad - ((v - min) / rng) * (H - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const color = T.blue;
  const [lastX, lastY] = pts[pts.length - 1].split(",");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: W, height: H, display: "block" }}>
      <polyline fill="none" stroke={color} strokeWidth={1.6} points={pts.join(" ")} />
      <circle cx={lastX} cy={lastY} r={2} fill={color} />
    </svg>
  );
}

// ─── Portfolio trend line chart ───────────────────────────────────────────────

function TrendLineChart({ allMonths, snapshotMonths, aggregateByMonth, workerType }) {
  const W = 620, H = 140;
  const PAD = { top: 20, right: 20, bottom: 28, left: 50 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const dataMonths = allMonths.filter((m) => snapshotMonths.has(m));
  const values = dataMonths.map((m) => {
    const agg = aggregateByMonth.get(m);
    return agg ? (getValue(agg, workerType) ?? 0) : 0;
  });

  const maxVal = values.length ? Math.max(...values, 1) : 1;
  // Floor the y-axis at 90% of the lowest data point so the line fills the
  // plot area instead of hugging the top. Clamped to 0 so the axis never
  // shows a negative floor for naturally non-negative headcount series.
  const rawMin = values.length ? Math.min(...values) : 0;
  const minVal = Math.max(0, Math.floor(rawMin * 0.9));
  const valRange = maxVal - minVal || 1;

  const pendingMonths = allMonths.filter((m) => !snapshotMonths.has(m));

  const xForMonth = (m) => {
    const idx = allMonths.indexOf(m);
    if (idx < 0) return null;
    return PAD.left + (idx / Math.max(allMonths.length - 1, 1)) * chartW;
  };

  const yForVal = (v) =>
    PAD.top + chartH - ((v - minVal) / valRange) * chartH;

  const gridY1 = PAD.top + chartH * 0;
  const gridY2 = PAD.top + chartH * 0.33;
  const gridY3 = PAD.top + chartH * 0.67;
  const gridVal1 = Math.round(maxVal);
  const gridVal2 = Math.round(minVal + valRange * 0.67);
  const gridVal3 = Math.round(minVal + valRange * 0.33);

  const polyPoints = dataMonths.map((m) => {
    const x = xForMonth(m);
    const agg = aggregateByMonth.get(m);
    const v = agg ? (getValue(agg, workerType) ?? 0) : 0;
    const y = yForVal(v);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  const pendingLeft = pendingMonths.length
    ? (xForMonth(pendingMonths[0]) ?? PAD.left)
    : null;
  const pendingRight = pendingMonths.length
    ? (xForMonth(pendingMonths[pendingMonths.length - 1]) ?? PAD.left) + chartW / allMonths.length
    : null;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      style={{ display: "block" }}
      role="img"
      aria-label="Portfolio headcount trend over time"
    >
      {/* Gridlines */}
      <g stroke={T.bg} strokeWidth={1}>
        <line x1={PAD.left} y1={gridY1} x2={W - PAD.right} y2={gridY1} />
        <line x1={PAD.left} y1={gridY2} x2={W - PAD.right} y2={gridY2} />
        <line x1={PAD.left} y1={gridY3} x2={W - PAD.right} y2={gridY3} />
      </g>
      {/* Y labels */}
      <g fill={T.light} fontSize={10} textAnchor="end">
        <text x={PAD.left - 6} y={gridY1 + 4}>{fmtN(gridVal1)}</text>
        <text x={PAD.left - 6} y={gridY2 + 4}>{fmtN(gridVal2)}</text>
        <text x={PAD.left - 6} y={gridY3 + 4}>{fmtN(gridVal3)}</text>
      </g>
      {/* Pending zone */}
      {pendingLeft != null && (
        <>
          <rect
            x={pendingLeft}
            y={PAD.top}
            width={pendingRight - pendingLeft}
            height={chartH}
            fill={T.hoverBg}
          />
          <text
            x={(pendingLeft + pendingRight) / 2}
            y={PAD.top + chartH / 2 + 4}
            fill={T.borderDark}
            fontSize={10}
            textAnchor="middle"
          >
            awaiting data
          </text>
        </>
      )}
      {/* X-axis labels */}
      <g fill={T.light} fontSize={11} textAnchor="middle">
        {allMonths.map((m) => {
          const x = xForMonth(m);
          if (x == null) return null;
          return (
            <text key={m} x={x} y={H - 4}>{fmtMonthShort(m)}</text>
          );
        })}
      </g>
      {/* Trend line */}
      {dataMonths.length > 1 && (
        <polyline
          fill="none"
          stroke={T.blue}
          strokeWidth={2.5}
          strokeLinejoin="round"
          points={polyPoints}
        />
      )}
      {/* Data points + labels */}
      {dataMonths.map((m) => {
        const x = xForMonth(m);
        const agg = aggregateByMonth.get(m);
        const v = agg ? (getValue(agg, workerType) ?? 0) : 0;
        const y = yForVal(v);
        const provenanceParts = [
          fmtMonthLabel(m) + ": " + fmtN(v),
          agg?.sourceFile ? "File: " + agg.sourceFile : null,
          agg?.appliedBy && agg.appliedBy !== "System" ? "Imported by: " + agg.appliedBy : null,
          "As-of: " + (agg?.snapshotDate ?? m),
        ].filter(Boolean);
        return (
          <g key={m}>
            <circle cx={x} cy={y} r={3.5} fill={T.blue}>
              <title>{provenanceParts.join("\n")}</title>
            </circle>
            <text x={x} y={y - 7} fontSize={10} textAnchor="middle" fontWeight={700} fill={T.dark}>
              {fmtN(v)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Dimension trend chart (multi-series line) ────────────────────────────────
//
// HEADCOUNT-TREND-NAV-STREAMLINE: Display:Chart mode for dimension breakdowns.
// Plots one line per top-N group label (capped at DIMENSION_CHART_CAP) using
// rollup rows already returned by the API.
// labelKey: property name on each row for the group label
//   ("division" | "cohortLabel" | "geoLabel")
const CHART_SERIES_COLORS = [
  T.blue, T.purpleDeep, T.utilGreenText, T.amber, T.red, T.teal, T.green, T.ragAmber,
];
const DIMENSION_CHART_CAP = 8;

function DimensionTrendChart({ rollupRows, labelKey, allMonths, snapshotMonths, measure }) {
  const W = 620, H = 200;
  const PAD = { top: 20, right: 110, bottom: 28, left: 50 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const dataMonths = allMonths.filter((m) => snapshotMonths.has(m));

  // Build label → month → row lookup for O(1) access
  const rowsByLabelMonth = useMemo(() => {
    warnBadRows("DimensionTrendChart", rollupRows, [labelKey, "count", "snapshotDate"]);
    const map = new Map();
    for (const row of rollupRows) {
      const ym = row.snapshotDate ? row.snapshotDate.slice(0, 7) : null;
      if (!ym) continue;
      const label = row[labelKey];
      if (label == null) continue;
      map.set(`${label}|${ym}`, row);
    }
    return map;
  }, [rollupRows, labelKey]);

  // Top-N labels by count in the latest snapshot month
  const latestMonth = dataMonths[dataMonths.length - 1];
  const topLabels = useMemo(() => {
    if (!latestMonth) return [];
    return rollupRows
      .filter((r) => r.snapshotDate && r.snapshotDate.slice(0, 7) === latestMonth && r[labelKey] != null)
      .slice()
      .sort((a, b) => b.count - a.count)
      .map((r) => r[labelKey])
      .slice(0, DIMENSION_CHART_CAP);
  }, [rollupRows, labelKey, latestMonth]);

  if (topLabels.length === 0 || dataMonths.length === 0) {
    return (
      <div style={{ textAlign: "center", color: T.mid, fontSize: T.fsBase, padding: "32px 0" }}>
        No data available for chart view.
      </div>
    );
  }

  const series = topLabels.map((label) => ({
    label,
    values: dataMonths.map((m) => {
      const row = rowsByLabelMonth.get(`${label}|${m}`);
      if (!row) return null;
      return getRollupValue(row, measure);
    }),
  }));

  const allVals = series.flatMap((s) => s.values.filter((v) => v != null));
  const maxVal  = allVals.length ? Math.max(...allVals, 1) : 1;
  // Floor the y-axis at 90% of the lowest value across all series so no single
  // dominant series squishes the remaining ones against the bottom of the chart.
  // Clamped to 0 so the axis never shows a negative floor.
  const rawMin  = allVals.length ? Math.min(...allVals) : 0;
  const minVal  = Math.max(0, Math.floor(rawMin * 0.9));
  const valRange = (maxVal - minVal) || 1;

  const xForIdx = (i) => PAD.left + (i / Math.max(dataMonths.length - 1, 1)) * chartW;
  const yForVal = (v) => PAD.top + chartH - ((v - minVal) / valRange) * chartH;

  return (
    <div style={{ overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ display: "block" }}
        role="img"
        aria-label="Headcount trend by dimension over time"
      >
        {/* Gridlines */}
        <g stroke={T.bg} strokeWidth={1}>
          <line x1={PAD.left} y1={PAD.top}                    x2={W - PAD.right} y2={PAD.top} />
          <line x1={PAD.left} y1={PAD.top + chartH * 0.5}     x2={W - PAD.right} y2={PAD.top + chartH * 0.5} />
          <line x1={PAD.left} y1={PAD.top + chartH}            x2={W - PAD.right} y2={PAD.top + chartH} />
        </g>
        {/* Y labels */}
        <g fill={T.light} fontSize={T.fsXxs} textAnchor="end">
          <text x={PAD.left - 6} y={PAD.top + 4}>{fmtN(maxVal)}</text>
          <text x={PAD.left - 6} y={PAD.top + chartH * 0.5 + 4}>{fmtN(Math.round((maxVal + minVal) / 2))}</text>
          <text x={PAD.left - 6} y={PAD.top + chartH + 4}>{fmtN(minVal)}</text>
        </g>
        {/* X labels */}
        <g fill={T.light} fontSize={T.fsXs} textAnchor="middle">
          {dataMonths.map((m, i) => (
            <text key={m} x={xForIdx(i)} y={H - 4}>{fmtMonthShort(m)}</text>
          ))}
        </g>
        {/* Series lines */}
        {series.map(({ label, values }, si) => {
          const color = CHART_SERIES_COLORS[si % CHART_SERIES_COLORS.length];
          // Build contiguous polyline segments (null values break a segment)
          const segments = [];
          let cur = [];
          for (let i = 0; i < values.length; i++) {
            const v = values[i];
            if (v != null) {
              cur.push(`${xForIdx(i).toFixed(1)},${yForVal(v).toFixed(1)}`);
            } else if (cur.length > 0) {
              segments.push(cur.join(" "));
              cur = [];
            }
          }
          if (cur.length > 0) segments.push(cur.join(" "));
          // End dot on last non-null point
          let lastNonNullIdx = -1;
          for (let i = values.length - 1; i >= 0; i--) {
            if (values[i] != null) { lastNonNullIdx = i; break; }
          }
          return (
            <g key={label}>
              {segments.map((pts, j) => (
                <polyline key={j} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" points={pts} />
              ))}
              {lastNonNullIdx >= 0 && (
                <circle
                  cx={xForIdx(lastNonNullIdx)}
                  cy={yForVal(values[lastNonNullIdx])}
                  r={3}
                  fill={color}
                />
              )}
            </g>
          );
        })}
      </svg>
      <ViewScaffold
        surface="neutral"
        testid="dimension-chart-legend"
        legendItems={topLabels.map((label, si) => ({
          label,
          indicator: { type: "line", color: CHART_SERIES_COLORS[si % CHART_SERIES_COLORS.length] },
        }))}
      />
    </div>
  );
}

// ─── Status tag ───────────────────────────────────────────────────────────────

function StatusTag({ label }) {
  if (!label) return null;
  return (
    <span style={{
      fontSize: T.fsXxs,
      fontWeight: T.fwBold,
      padding: "1px 6px",
      borderRadius: 6,
      background: T.lightGray,
      color: T.mid,
      marginLeft: 6,
      verticalAlign: "middle",
      display: "inline-block",
    }}>
      {label}
    </span>
  );
}


// ─── Distributed / remote geography panel ────────────────────────────────────
//
// HEADCOUNT-TREND-V2-S1: shows geo clusters for the latest snapshot month.
// HEADCOUNT-TREND-V2-S2: accepts optional `selectedMonth` prop for geography
//   view so the user can step through loaded snapshot months independently.
// Blue bar = geo matches a known market name (has a Ripple office).
// Violet bar = no match → planning cue for possible space demand.
// Collapsed when distributedRollup is empty (pre-re-ingest installs).

// ─── Compact month-selector dropdown ─────────────────────────────────────────
//
// Shared control for Geography, Division, and M&A views. Renders a native
// <select> styled to match the toolbar. Hidden when only one snapshot month
// exists. data-testid="hc-month-dropdown" on the outer wrapper.

function MonthDropdown({ snapMonths, value, onChange }) {
  const months = [...snapMonths].sort();
  if (months.length <= 1) return null;
  const effectiveValue = value && snapMonths.has(value) ? value : months[months.length - 1];
  const [isOpen, setIsOpen] = useState(false);
  const options = months.map((m) => ({ value: m, label: fmtMonthLabel(m) }));
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="hc-month-dropdown">
      <FilterDropdown
        id="hc-month"
        label="Month"
        currentVal={fmtMonthLabel(effectiveValue)}
        isFiltered={false}
        options={options}
        selectedValue={effectiveValue}
        isOpen={isOpen}
        onToggle={() => setIsOpen((o) => !o)}
        onSelect={(v) => { onChange(v); setIsOpen(false); }}
      />
    </div>
  );
}

function DistributedPanel({ distributedRollup, offices, latestSnapshotDate, selectedMonth, workerType = "total", searchQuery = "", sortField = "value", sortDir = "desc" }) {
  // When selectedMonth is provided (geography view), use it; otherwise default
  // to the latest snapshot month (original side-panel behaviour).
  const latestMonth = latestSnapshotDate ? latestSnapshotDate.slice(0, 7) : null;
  const activeMonth = selectedMonth ?? latestMonth;
  const latestRows = useMemo(() => {
    if (!activeMonth) return [];
    const candidate = distributedRollup
      .filter((r) => r.snapshotDate && r.snapshotDate.slice(0, 7) === activeMonth);
    warnBadRows("DistributedPanel", candidate, ["geoLabel", "count"]);
    let filtered = candidate.filter((r) => r.geoLabel != null);
    if (searchQuery.trim()) {
      filtered = filtered.filter((r) => matchesFoldedQuery(searchQuery, r.geoLabel));
    }
    return filtered.slice().sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortField === "name") {
        return a.geoLabel.localeCompare(b.geoLabel) * dir;
      }
      return (getRollupValue(b, workerType) - getRollupValue(a, workerType)) * (sortDir === "asc" ? -1 : 1);
    });
  }, [distributedRollup, activeMonth, workerType, searchQuery, sortField, sortDir]);

  if (latestRows.length === 0) return null;

  // Build set of known market names from offices (case-insensitive).
  const marketNames = useMemo(() => {
    const s = new Set();
    for (const o of offices) {
      if (o.market) s.add(foldText(o.market));
      if (o.alias)  s.add(foldText(o.alias));
    }
    return s;
  }, [offices]);

  const hasOffice = (geoLabel) => marketNames.has(foldText(geoLabel));

  const maxCount = latestRows.length ? getRollupValue(latestRows[0], workerType) : 1;

  const noOfficeClusters = latestRows.filter((r) => !hasOffice(r.geoLabel));

  const allZero = latestRows.length > 0 && latestRows.every(r => getRollupValue(r, workerType) === 0);

  if (allZero && workerType === "total") return null;

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: "16px 20px",
      marginBottom: 18,
    }}>
      <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 2 }}>
        Distributed / remote workforce
      </div>
      <div style={{ fontSize: T.fsXs, color: T.light, marginBottom: 12 }}>
        Geo clusters for {activeMonth ? activeMonth : "latest snapshot"} · workers with no OfficeSpace ID.
        <span style={{ marginLeft: 8 }}>
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: T.blue, verticalAlign: "middle", marginRight: 4 }} />has Ripple office
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: T.purpleDeep, verticalAlign: "middle", marginLeft: 10, marginRight: 4 }} />no Ripple office
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {latestRows.map((row) => {
          const val = getRollupValue(row, workerType);
          const pct = maxCount > 0 ? (val / maxCount) * 100 : 0;
          const isKnown = hasOffice(row.geoLabel);
          const barColor = isKnown ? T.blue : T.purpleDeep;
          return (
            <div key={row.geoLabel} data-testid={`hc-geo-row-${row.geoLabel}`} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 130, fontSize: T.fsXs, color: T.dark, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={row.geoLabel}>
                {row.geoLabel}
              </div>
              <div style={{ flex: 1, height: 10, background: T.bg, borderRadius: 5, position: "relative", overflow: "hidden" }}>
                <div style={{
                  position: "absolute", left: 0, top: 0, height: "100%",
                  width: `${pct}%`, background: barColor, borderRadius: 5,
                  transition: "width 0.2s",
                }} />
              </div>
              <div style={{ width: 36, fontSize: T.fsXs, color: T.mid, textAlign: "right", flexShrink: 0 }}>
                {kFmtGeo(val)}
              </div>
            </div>
          );
        })}
      </div>
      {noOfficeClusters.length > 0 && (
        <div style={{
          marginTop: 12, padding: "8px 12px", borderRadius: 8,
          background: T.purpleBg, color: T.purpleDeep, fontSize: T.fsXs,
        }}>
          <strong>{noOfficeClusters.length} cluster{noOfficeClusters.length !== 1 ? "s" : ""} with no Ripple office</strong>
          {" "}({noOfficeClusters.map((r) => r.geoLabel).join(", ")}) - watch for future space demand in these locations.
        </div>
      )}
    </div>
  );
}

// ─── M&A / acquired-entity keyword detection ─────────────────────────────────
//
// Divisions whose label matches one of these keywords (case-insensitive) are
// treated as M&A / acquired-entity cohorts and coloured with the purple token.
// Extend this list as new acquisitions are onboarded.

const MA_KEYWORDS = ["prime", "gtreasury"];

function isMaDivision(divisionLabel) {
  if (!divisionLabel) return false;
  const lower = divisionLabel.toLowerCase();
  return MA_KEYWORDS.some((kw) => lower.includes(kw));
}

// ─── K-suppression helpers ───────────────────────────────────────────────────
// Entries below the threshold are displayed as "<N" instead of a numeric value.
// The raw number is still used for bar width calculation so bars remain
// proportional even when labels are suppressed.
//
// K_THRESHOLD     (10) — Division and M&A panels.
// K_THRESHOLD_GEO  (5) — Geography / DistributedPanel only.

const K_THRESHOLD     = 10;
const K_THRESHOLD_GEO = 5;

function kFmt(n) {
  if (n == null) return "—";
  return Number(n) < K_THRESHOLD ? "<10" : fmtN(n);
}

function kFmtGeo(n) {
  if (n == null) return "—";
  return Number(n) < K_THRESHOLD_GEO ? "<5" : fmtN(n);
}

// ─── Worker-type value helper ─────────────────────────────────────────────────
// Selects the right field from a rollup row given the current workerType filter.

function getRollupValue(row, workerType) {
  if (!row) return 0;
  switch (workerType) {
    case "ft":         return row.ft  ?? 0;
    case "contractor": return (row.cw ?? 0) + (row.ic ?? 0);
    default:           return row.count ?? ((row.ft ?? 0) + (row.cw ?? 0) + (row.ic ?? 0));
  }
}

// ─── Division view panel ──────────────────────────────────────────────────────
//
// HEADCOUNT-TREND-V2-S2: portfolio-level division bar chart.
// HEADCOUNT-TREND-V2-S3: adds group-level drill-down: clicking a division
//   row expands a sub-list showing each Group within that division.
//   Worker-type narrowing is supported via the workerType prop.
//
// Renders horizontal bar rows sorted descending by count.
// M&A cohort entries → T.purpleDeep fill; all others → T.blue.
// Collapses to an empty-state card when divisionRollup is empty.

function DivisionPanel({ divisionRollup, groupRollup, latestSnapshotDate, selectedMonth, workerType = "total", searchQuery = "", sortField = "value", sortDir = "desc" }) {
  const latestMonth = latestSnapshotDate ? latestSnapshotDate.slice(0, 7) : null;
  // selectedMonth (from shared toolbar dropdown) overrides latestMonth when provided.
  const activeMonth = selectedMonth ?? latestMonth;

  // drillDivision: which division is currently expanded for group drill-down.
  const [drillDivision, setDrillDivision] = useState(null);

  const rows = useMemo(() => {
    if (!activeMonth) return [];
    const candidate = divisionRollup
      .filter((r) => r.snapshotDate && r.snapshotDate.slice(0, 7) === activeMonth);
    warnBadRows("DivisionPanel", candidate, ["division", "count"]);
    let filtered = candidate.filter((r) => r.division != null);
    if (searchQuery.trim()) {
      filtered = filtered.filter((r) => matchesFoldedQuery(searchQuery, r.division));
    }
    return filtered.slice().sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortField === "name") {
        return a.division.localeCompare(b.division) * dir;
      }
      return (getRollupValue(b, workerType) - getRollupValue(a, workerType)) * (sortDir === "asc" ? -1 : 1);
    });
  }, [divisionRollup, activeMonth, workerType, searchQuery, sortField, sortDir]);

  // Group rows for the drilled division, sorted descending.
  const drillRows = useMemo(() => {
    if (!drillDivision || !activeMonth) return [];
    return groupRollup
      .filter((r) => r.snapshotDate && r.snapshotDate.slice(0, 7) === activeMonth && r.division === drillDivision)
      .slice()
      .sort((a, b) => getRollupValue(b, workerType) - getRollupValue(a, workerType));
  }, [groupRollup, drillDivision, activeMonth, workerType]);

  if (rows.length === 0) {
    return (
      <div style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        padding: "36px 24px",
        textAlign: "center",
        marginBottom: 18,
      }}>
        <div style={{ fontSize: T.fsLg, marginBottom: 8 }}>📊</div>
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
          No data yet
        </div>
        <div style={{ fontSize: T.fsBase, color: T.mid }}>
          Re-ingest headcount files to populate this view.
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...rows.map((r) => getRollupValue(r, workerType)), 1);

  // ── Drill-in view: show group breakdown for drillDivision ──────────────────
  if (drillDivision) {
    const drilledRow = rows.find((r) => r.division === drillDivision);
    const isMa = isMaDivision(drillDivision);
    const barColor = isMa ? T.purpleDeep : T.blue;
    const drillMax = Math.max(...drillRows.map((r) => getRollupValue(r, workerType)), 1);
    return (
      <div style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        padding: "16px 20px",
        marginBottom: 18,
      }}>
        {/* Back control */}
        <button
          data-testid="hc-division-drill-back"
          onClick={() => setDrillDivision(null)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: 0, marginBottom: 8,
            display: "flex", alignItems: "center", gap: 4,
            fontSize: T.fsXs, color: T.blue, fontWeight: T.fwSemibold,
          }}
        >
          ← All divisions
        </button>
        <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 2 }}>
          {drillDivision}
          {drilledRow && (
            <span style={{ fontSize: T.fsXs, fontWeight: T.fwNormal, color: T.mid, marginLeft: 8 }}>
              {fmtN(getRollupValue(drilledRow, workerType))} total
            </span>
          )}
        </div>
        <div style={{ fontSize: T.fsXs, color: T.light, marginBottom: 12 }}>
          Groups · {latestMonth ? fmtMonthLabel(latestMonth) : "latest snapshot"} · counts &lt;{K_THRESHOLD} are suppressed.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {drillRows.length === 0 ? (
            <div style={{ fontSize: T.fsXs, color: T.light, textAlign: "center", padding: "16px 0" }}>
              No group data for this division.
            </div>
          ) : drillRows.map((gr) => {
            const gVal = getRollupValue(gr, workerType);
            const gPct = drillMax > 0 ? (gVal / drillMax) * 100 : 0;
            return (
              <div
                key={`${gr.division}||${gr.group}`}
                data-testid={`hc-group-row-${gr.division}-${gr.group}`}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <div style={{
                  width: 180, fontSize: T.fsXs, color: T.dark, flexShrink: 0,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }} title={gr.group}>
                  {gr.group}
                </div>
                <div style={{ flex: 1, height: 10, background: T.bg, borderRadius: 5, position: "relative", overflow: "hidden" }}>
                  <div style={{
                    position: "absolute", left: 0, top: 0, height: "100%",
                    width: `${gPct}%`, background: barColor, borderRadius: 5,
                    transition: "width 0.2s",
                  }} />
                </div>
                <div style={{ width: 44, fontSize: T.fsXs, color: T.mid, textAlign: "right", flexShrink: 0, fontWeight: T.fwSemibold }}>
                  {kFmt(gVal)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Top-level division list ─────────────────────────────────────────────────
  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: "16px 20px",
      marginBottom: 18,
    }}>
      <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 2 }}>
        Headcount by division
      </div>
      <div style={{ fontSize: T.fsXs, color: T.light, marginBottom: 12 }}>
        Portfolio-level · {latestMonth ? fmtMonthLabel(latestMonth) : "latest snapshot"} · total across all offices and remote.
        <span style={{ marginLeft: 8 }}>
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: T.blue, verticalAlign: "middle", marginRight: 4 }} />core
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: T.purpleDeep, verticalAlign: "middle", marginLeft: 10, marginRight: 4 }} />M&amp;A / acquired
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((row) => {
          const val = getRollupValue(row, workerType);
          const pct = maxCount > 0 ? (val / maxCount) * 100 : 0;
          const isMa = isMaDivision(row.division);
          const barColor = isMa ? T.purpleDeep : T.blue;
          const countColor = isMa ? T.purpleDeep : T.mid;
          const hasGroups = groupRollup.some((g) =>
            g.snapshotDate && g.snapshotDate.slice(0, 7) === latestMonth && g.division === row.division
          );
          return (
            <div
              key={row.division}
              data-testid={`hc-division-row-${row.division}`}
              onClick={hasGroups ? () => setDrillDivision(row.division) : undefined}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                cursor: hasGroups ? "pointer" : "default",
                borderRadius: 6,
                padding: "2px 4px",
                marginLeft: -4,
              }}
            >
              <div style={{
                width: 180, fontSize: T.fsXs, color: T.dark, flexShrink: 0,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                display: "flex", alignItems: "center", gap: 4,
              }} title={row.division}>
                {hasGroups && (
                  <span style={{ fontSize: T.fsXxs, color: T.mid, lineHeight: 1 }}>▸</span>
                )}
                {row.division}
              </div>
              <div style={{ flex: 1, height: 10, background: T.bg, borderRadius: 5, position: "relative", overflow: "hidden" }}>
                <div style={{
                  position: "absolute", left: 0, top: 0, height: "100%",
                  width: `${pct}%`, background: barColor, borderRadius: 5,
                  transition: "width 0.2s",
                }} />
              </div>
              <div style={{ width: 44, fontSize: T.fsXs, color: countColor, textAlign: "right", flexShrink: 0, fontWeight: T.fwSemibold }}>
                {fmtN(val)}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, fontSize: T.fsXs, color: T.light, lineHeight: 1.6 }}>
        Click a division to see its group breakdown.
      </div>
    </div>
  );
}

// ─── Acquired Company panel ───────────────────────────────────────────────────
//
// HEADCOUNT-TREND-V2-S3: portfolio-level M&A / acquired-entity breakdown.
// Shows headcount by Acquired Company label for the latest snapshot month.
// "Ripple (organic)" is the baseline organic headcount row.
// Purple fill for M&A entities; blue for organic (Ripple (organic)).
// Entries <10 are k-suppressed (displayed as "<10").

const ORGANIC_LABEL = "Ripple (organic)";

function AcquiredCompanyPanel({ acquiredCompanyRollup, latestSnapshotDate, selectedMonth, workerType = "total", searchQuery = "", sortField = "value", sortDir = "desc" }) {
  const latestMonth = latestSnapshotDate ? latestSnapshotDate.slice(0, 7) : null;
  const activeMonth = selectedMonth ?? latestMonth;

  const rows = useMemo(() => {
    if (!activeMonth) return [];
    const candidate = acquiredCompanyRollup
      .filter((r) => r.snapshotDate && r.snapshotDate.slice(0, 7) === activeMonth);
    warnBadRows("AcquiredCompanyPanel", candidate, ["cohortLabel", "count"]);
    let filtered = candidate.filter((r) => r.cohortLabel != null);
    if (searchQuery.trim()) {
      filtered = filtered.filter((r) => matchesFoldedQuery(searchQuery, r.cohortLabel));
    }
    return filtered.slice().sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortField === "name") {
        return a.cohortLabel.localeCompare(b.cohortLabel) * dir;
      }
      return (getRollupValue(b, workerType) - getRollupValue(a, workerType)) * (sortDir === "asc" ? -1 : 1);
    });
  }, [acquiredCompanyRollup, activeMonth, workerType, searchQuery, sortField, sortDir]);

  if (rows.length === 0) {
    return (
      <div style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        padding: "36px 24px",
        textAlign: "center",
        marginBottom: 18,
      }}>
        <div style={{ fontSize: T.fsLg, marginBottom: 8 }}>🏢</div>
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
          No data yet
        </div>
        <div style={{ fontSize: T.fsBase, color: T.mid }}>
          Re-ingest headcount files with an "Acquired Company" column to populate this view.
        </div>
      </div>
    );
  }

  const maxVal = Math.max(...rows.map((r) => getRollupValue(r, workerType)), 1);

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: "16px 20px",
      marginBottom: 18,
    }}>
      <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 2 }}>
        Headcount by M&amp;A
      </div>
      <div style={{ fontSize: T.fsXs, color: T.light, marginBottom: 12 }}>
        Portfolio-level · {latestMonth ? fmtMonthLabel(latestMonth) : "latest snapshot"} · total across all offices and remote.
        <span style={{ marginLeft: 8 }}>
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: T.blue, verticalAlign: "middle", marginRight: 4 }} />organic
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: T.purpleDeep, verticalAlign: "middle", marginLeft: 10, marginRight: 4 }} />acquired
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((row) => {
          const val = getRollupValue(row, workerType);
          const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
          const isOrganic = row.cohortLabel === ORGANIC_LABEL;
          const barColor = isOrganic ? T.blue : T.purpleDeep;
          const countColor = isOrganic ? T.mid : T.purpleDeep;
          return (
            <div key={row.cohortLabel} data-testid={`hc-acquired-row-${row.cohortLabel}`} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 180, fontSize: T.fsXs, color: T.dark, flexShrink: 0,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }} title={row.cohortLabel}>
                {row.cohortLabel}
              </div>
              <div style={{ flex: 1, height: 10, background: T.bg, borderRadius: 5, position: "relative", overflow: "hidden" }}>
                <div style={{
                  position: "absolute", left: 0, top: 0, height: "100%",
                  width: `${pct}%`, background: barColor, borderRadius: 5,
                  transition: "width 0.2s",
                }} />
              </div>
              <div style={{ width: 44, fontSize: T.fsXs, color: countColor, textAlign: "right", flexShrink: 0, fontWeight: T.fwSemibold }}>
                {kFmt(val)}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, fontSize: T.fsXs, color: T.light, lineHeight: 1.6 }}>
        Entries with fewer than {K_THRESHOLD} people are suppressed. "Ripple (organic)" represents employees not attributed to an acquired entity.
      </div>
    </div>
  );
}

// ─── OfficeChartView — two-panel (area-fill line chart + biggest movers) ─────

function MoverRow({ label, locationId, delta, maxDelta }) {
  const isGain = delta > 0;
  const color  = T.mid;
  const pct    = maxDelta > 0 ? (Math.abs(delta) / maxDelta) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 88, flexShrink: 0 }}>
        <div
          title={label}
          style={{
            fontSize: T.fsXxs, color: T.dark,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
        {locationId && locationId !== label && (
          <div style={{
            fontSize: T.fsXxs, color: T.light, marginTop: 1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {locationId}
          </div>
        )}
      </div>
      <div style={{ flex: 1, height: 7, background: T.bg, borderRadius: 4, position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", left: 0, top: 0, height: "100%",
          width: `${pct}%`, background: color, borderRadius: 4,
        }} />
      </div>
      <div style={{ width: 36, fontSize: T.fsXxs, fontWeight: T.fwBold, color, textAlign: "right", flexShrink: 0 }}>
        {isGain ? `+${fmtN(delta)}` : fmtN(delta)}
      </div>
    </div>
  );
}

function OfficeChartView({
  allMonths,
  snapshotMonths,
  workerType,
  offices,
  snapByLocMonth,
  officeMeasureByMonth,
  measureTotalValues,
  pendingCount,
}) {
  const dataMonths = useMemo(
    () => allMonths.filter((m) => snapshotMonths.has(m)),
    [allMonths, snapshotMonths],
  );

  const inOfficeVals = useMemo(() =>
    dataMonths.map((m) => officeMeasureByMonth.get(m)?.value ?? null),
  [dataMonths, officeMeasureByMonth]);

  const totalVals = useMemo(() =>
    dataMonths.map((m) => {
      const monthIndex = allMonths.indexOf(m);
      return measureTotalValues[monthIndex]?.value ?? null;
    }),
  [allMonths, dataMonths, measureTotalValues]);

  const latestM = dataMonths[dataMonths.length - 1] ?? null;
  const prevM   = dataMonths[dataMonths.length - 2] ?? null;

  const moverData = useMemo(() => {
    if (!latestM || !prevM) return { gainers: [], shrinking: [] };
    const deltas = offices.map((office) => {
      const getVal = (mo) => {
        if (!mo) return null;
        const snap = snapByLocMonth.get(`${office.locationId}|${mo}`);
        if (!snap) return null;
        return getValue(snap, workerType);
      };
      const latestVal = getVal(latestM);
      const prevVal   = getVal(prevM);
      if (latestVal == null || prevVal == null) return null;
      return { office, delta: latestVal - prevVal };
    }).filter(Boolean);
    const gainers   = [...deltas].sort((a, b) => b.delta - a.delta).filter((d) => d.delta > 0).slice(0, 3);
    const shrinking = [...deltas].sort((a, b) => a.delta - b.delta).filter((d) => d.delta < 0).slice(0, 3);
    return { gainers, shrinking };
  }, [offices, latestM, prevM, workerType, snapByLocMonth]);

  const maxMoverDelta = Math.max(
    ...moverData.gainers.map((d) => d.delta),
    ...moverData.shrinking.map((d) => Math.abs(d.delta)),
    1,
  );

  // SVG geometry
  const W = 520, H = 190;
  const PAD = { top: 24, right: 68, bottom: 28, left: 52 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top  - PAD.bottom;

  const allVals  = [...inOfficeVals, ...totalVals].filter((v) => v != null);
  const maxVal   = allVals.length ? Math.max(...allVals) : 1;
  // Floor at 90% of the lowest value so lines fill the plot rather than
  // hugging the top when all values are clustered near the same level.
  const rawMinOC = allVals.length ? Math.min(...allVals) : 0;
  const minValOC = Math.max(0, Math.floor(rawMinOC * 0.9));
  const valRange = (maxVal - minValOC) || 1;

  const xFn = (i) => PAD.left + (dataMonths.length > 1 ? i / (dataMonths.length - 1) : 0.5) * chartW;
  const yFn = (v) => PAD.top  + chartH - ((v - minValOC) / valRange) * chartH;

  const getSegments = (values) => {
    const segs = []; let cur = [];
    for (let i = 0; i < values.length; i++) {
      if (values[i] != null) {
        cur.push({ index: i, value: values[i] });
      } else if (cur.length > 0) {
        segs.push(cur); cur = [];
      }
    }
    if (cur.length > 0) segs.push(cur);
    return segs;
  };
  const officeSegments = getSegments(inOfficeVals);
  const officeLinePoints = (segment) => segment
    .map(({ index, value }) => `${xFn(index).toFixed(1)},${yFn(value).toFixed(1)}`)
    .join(" ");
  const officeAreaPoints = (segment) => [
    `${xFn(segment[0].index).toFixed(1)},${(PAD.top + chartH).toFixed(1)}`,
    ...segment.map(({ index, value }) => `${xFn(index).toFixed(1)},${yFn(value).toFixed(1)}`),
    `${xFn(segment[segment.length - 1].index).toFixed(1)},${(PAD.top + chartH).toFixed(1)}`,
  ].join(" ");
  const totalSegments = getSegments(totalVals)
    .filter((segment) => segment.length > 1)
    .map((segment) => segment
      .map(({ index, value }) => `${xFn(index).toFixed(1)},${yFn(value).toFixed(1)}`)
      .join(" "));

  const gridVals = [0, 0.33, 0.67, 1.0].map((f) => Math.round(minValOC + f * valRange));
  const xStep    = Math.max(1, Math.ceil(dataMonths.length / 8));
  const lastIdx  = dataMonths.length - 1;
  const lastX    = lastIdx >= 0 ? xFn(lastIdx) : null;
  const lastIn   = inOfficeVals[lastIdx] ?? null;
  const lastTot  = totalVals[lastIdx] ?? null;
  const hasMoverData = moverData.gainers.length > 0 || moverData.shrinking.length > 0;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr", gap: 16, alignItems: "stretch" }}>
      {/* Left panel: area-fill line chart */}
      <div style={{ minWidth: 0 }}>
        {/* Legend row — above the SVG */}
        <ViewScaffold
          surface="neutral"
          testid="portfolio-trend-legend"
          legendItems={[
            { label: "In office (assigned)", indicator: { type: "line", color: T.blue,  dashed: false } },
            { label: "Total company",        indicator: { type: "line", color: T.light, dashed: true  } },
          ]}
        />
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          style={{ display: "block", overflow: "visible" }}
          role="img"
          aria-label="Portfolio headcount trend over time"
        >
          {/* Gridlines — solid light horizontal lines */}
          <g stroke={T.border} strokeWidth={0.8}>
            {gridVals.map((gv, i) => (
              <line key={i} x1={PAD.left} y1={yFn(gv)} x2={W - PAD.right} y2={yFn(gv)} />
            ))}
          </g>
          {/* Y-axis labels */}
          <g fill={T.light} fontSize={10} textAnchor="end">
            {gridVals.map((gv, i) => (
              <text key={i} x={PAD.left - 6} y={yFn(gv) + 4}>{kFmt(gv)}</text>
            ))}
          </g>
          {/* Area fill under in-office line */}
          {officeSegments.filter((segment) => segment.length > 1).map((segment, i) => (
            <polygon key={i} points={officeAreaPoints(segment)} fill={T.blue} fillOpacity={0.10} />
          ))}
          {/* In-office solid line */}
          {officeSegments.filter((segment) => segment.length > 1).map((segment, i) => (
            <polyline
              key={i}
              fill="none"
              stroke={T.blue}
              strokeWidth={2.5}
              strokeLinejoin="round"
              points={officeLinePoints(segment)}
            />
          ))}
          {officeSegments.filter((segment) => segment.length === 1).map((segment) => (
            <circle
              key={segment[0].index}
              cx={xFn(segment[0].index)}
              cy={yFn(segment[0].value)}
              r={4}
              fill={T.blue}
            />
          ))}
          {/* Total company dashed reference — distinct color so it doesn't blend with the solid line */}
          {totalSegments.map((pts, i) => (
            <polyline key={i} fill="none" stroke={T.light} strokeWidth={1.5}
              strokeDasharray="5,3" strokeLinejoin="round" points={pts} />
          ))}
          {/* X-axis month labels */}
          <g fill={T.light} fontSize={10} textAnchor="middle">
            {dataMonths.map((m, i) => {
              if (i % xStep !== 0 && i !== dataMonths.length - 1) return null;
              return <text key={m} x={xFn(i)} y={H - 4}>{fmtMonthShort(m)}</text>;
            })}
          </g>
          {/* End-value labels */}
          {lastX != null && lastIn != null && (
            <text data-testid="hc-office-chart-in-office-last" x={lastX + 5} y={yFn(lastIn) + 4} fontSize={10} fontWeight={700} fill={T.blue} textAnchor="start">
              {fmtN(lastIn)}
            </text>
          )}
          {lastX != null && lastTot != null && lastTot !== lastIn && (
            <text data-testid="hc-office-chart-company-last" x={lastX + 5} y={yFn(lastTot) + 4} fontSize={9} fill={T.light} textAnchor="start">
              {fmtN(lastTot)}
            </text>
          )}
        </svg>
        {pendingCount > 0 && (
          <div style={{ fontSize: T.fsXxs, color: T.light, marginTop: 3 }}>
            Pending months load once received from HR.
          </div>
        )}
      </div>

      {/* Right panel: biggest movers */}
      <div>
        <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 2 }}>
          Biggest movers
        </div>
        <div style={{ fontSize: T.fsXxs, color: T.light, marginBottom: 10 }}>
          {latestM ? fmtMonthLabel(latestM) : ""} · in-office
        </div>
        {!hasMoverData ? (
          <div style={{ fontSize: T.fsXs, color: T.light }}>
            Need ≥ 2 loaded months to show movers.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {moverData.gainers.length > 0 && (
              <div>
                <div style={{ fontSize: T.fsXxs, color: T.light, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                  Growing
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {moverData.gainers.map(({ office, delta }) => (
                    <MoverRow key={office.locationId} label={office.alias || office.locationId} locationId={office.locationId} delta={delta} maxDelta={maxMoverDelta} />
                  ))}
                </div>
              </div>
            )}
            {moverData.shrinking.length > 0 && (
              <div>
                <div style={{ fontSize: T.fsXxs, color: T.light, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                  Shrinking
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {moverData.shrinking.map(({ office, delta }) => (
                    <MoverRow key={office.locationId} label={office.alias || office.locationId} locationId={office.locationId} delta={delta} maxDelta={maxMoverDelta} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

// Measure-aware aggregate accessors for the KPI strip.
// "all" → officeSum + unassigned (totalCompany) / officeSum
// "ft"  → ft column (office-assigned only; no distributed ft breakdown)
// "contractor" → cw + ic (office-assigned only)
function getMeasureOfficeCount(snap, m) {
  if (!snap) return null;
  if (m === "ft") return snap.ft ?? null;
  if (m === "contractor") return (snap.cw ?? 0) + (snap.ic ?? 0);
  return snap.officeSum ?? null;
}

export function getMeasureTotalCount(snap, m) {
  if (!snap) return null;
  if (m === "all") return snap.totalCompany ?? null;
  if (m === "ft") {
    // When the distributed split is unavailable, fall back to office-assigned only.
    // isMeasureTotalPartial() identifies these months so callers can show a "~" qualifier.
    const officeCount = snap.ft ?? null;
    if (officeCount == null) return null;
    return officeCount + (snap.unassignedFt ?? 0);
  }
  if (m === "contractor") {
    // Same fallback: use office-assigned cw+ic when no distributed split.
    return (snap.cw ?? 0) + (snap.ic ?? 0) + (snap.unassignedCw ?? 0);
  }
  return getMeasureOfficeCount(snap, m);
}

/**
 * Returns true when getMeasureTotalCount() for this snap+measure used the
 * office-assigned fallback (distributed/remote split was not available).
 * Used to render a "~" qualifier in the Total company row.
 */
export function isMeasureTotalPartial(snap, m) {
  if (!snap) return false;
  if (m === "ft")         return snap.ft != null && snap.unassignedFt == null;
  if (m === "contractor") return snap.unassignedCw == null;
  return false;
}

function getMeasureRemoteCount(snap, measure) {
  if (!snap) return null;
  if (measure === "ft") return snap.unassignedFt ?? null;
  if (measure === "contractor") return snap.unassignedCw ?? null;
  if (snap.unassigned != null) return snap.unassigned;
  if (snap.totalCompany != null && snap.officeSum != null) {
    return snap.totalCompany - snap.officeSum;
  }
  return null;
}

function makeMeasureTotalCell(snap, measure, officeMeasure) {
  if (!snap) return null;
  const remote = getMeasureRemoteCount(snap, measure);
  if (officeMeasure?.value == null) {
    return {
      value: null,
      partial: true,
      officePartial: true,
      remoteAvailable: remote != null,
    };
  }
  return {
    value: officeMeasure.value + (remote ?? 0),
    partial: officeMeasure.partial || remote == null,
    officePartial: officeMeasure.partial,
    remoteAvailable: remote != null,
  };
}

function getComparableMeasureMoM(currentCell, previousCell) {
  if (!currentCell || !previousCell) {
    return { delta: null, remoteBoundary: false };
  }
  const remoteBoundary = currentCell.remoteAvailable !== previousCell.remoteAvailable;
  if (currentCell.officePartial !== previousCell.officePartial) {
    return { delta: null, remoteBoundary: false };
  }
  if (currentCell.officeValue == null || previousCell.officeValue == null) {
    return { delta: null, remoteBoundary: false };
  }
  if (remoteBoundary) {
    // Compare the same in-office measure on both sides of a newly available
    // (or newly missing) remote split instead of reporting a false jump.
    return { delta: currentCell.officeValue - previousCell.officeValue, remoteBoundary: true };
  }
  if (currentCell.value == null || previousCell.value == null) {
    return { delta: null, remoteBoundary: false };
  }
  return {
    delta: currentCell.value - previousCell.value,
    remoteBoundary: false,
  };
}

const MEASURE_LABEL = {
  all:        "all workers",
  ft:         "full-time",
  contractor: "contractor",
};

/**
 * @param {{ onBack: () => void }} props
 */
export function HeadcountTrendReport({ onBack, prevReport = null, nextReport = null, onNavigateReport, onOpenLocation }) {
  const featureContext = useFeatureFlagContext();
  const eligibility = getReportNavEligibility(featureContext);
  const marketsEnabled = eligibility.markets;
  const officesEnabled = eligibility.offices;
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  // Display state encoded in URL + persisted to localStorage.
  const [rvs, setRvs] = useReportViewState({
    storageKey: REPORT_HC_TREND_KEY,
    fields: HC_VIEW_STATE_FIELDS,
  });

  // Derive named aliases to keep existing render code unchanged.
  const display    = rvs.view;
  const density    = rvs.density;
  const search     = rvs.q;
  const measure    = rvs.measure;
  const sortCol    = rvs.sort;
  const sortDir    = rvs.dir;
  const requestedViewBy = rvs.viewBy;
  const viewBy = (
    (!officesEnabled && requestedViewBy === "office") ||
    (!marketsEnabled && requestedViewBy === "geography")
  ) ? "division" : requestedViewBy;
  const selections = { region: rvs.region };

  // Office granularity — "office" shows individual offices; "market" collapses
  // to one synthetic row per market (sum of its offices per month).
  const [granularity, setGranularity] = useState("office");
  const displayGranularity = granularity;

  // Shared month selector for non-office dimension views (Geography, Division, M&A).
  // null = default to latest snapshot month.
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Dimension panel search / sort (for Division, M&A, Geography views).
  const [dimSearch, setDimSearch] = useState("");
  const [dimSortField, setDimSortField] = useState("value");
  const [dimSortDir, setDimSortDir] = useState("desc");


  // workerType alias — keeps all existing getValue/getRollupValue/classifyCell
  // callers working unchanged ("all" hits the default case in each switch).
  const workerType = measure;

  const highlightDate = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("reportDate") ?? null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getHeadcountTrendReport()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const aggregate = data?.aggregate ?? [];
  const offices   = data?.offices   ?? [];

  const allMonths = useMemo(() => buildAllMonths(aggregate), [aggregate]);
  const currentMonth = allMonths[allMonths.length - 1] ?? null;

  const snapshotMonths = useMemo(() => {
    const s = new Set();
    for (const a of aggregate) s.add(toYYYYMM(a.snapshotDate));
    return s;
  }, [aggregate]);

  const aggregateByMonth = useMemo(() => {
    const m = new Map();
    for (const a of aggregate) {
      const ym = toYYYYMM(a.snapshotDate);
      if (ym) m.set(ym, a);
    }
    return m;
  }, [aggregate]);

  const regions = useMemo(() => {
    const s = new Set();
    for (const o of offices) if (o.region) s.add(o.region);
    return [...s].sort();
  }, [offices]);

  const filteredOffices = useMemo(() => {
    let list = offices;
    const selRegions = marketsEnabled ? selections.region ?? [] : [];
    if (selRegions.length) list = list.filter((o) => selRegions.includes(o.region));
    if (search.trim()) {
      // Match on alias (location name), market name, AND location ID (e.g. "AMER-CHI-01").
      list = list.filter((o) =>
        matchesFoldedQuery(search, o.alias, ...(marketsEnabled ? [o.market] : []), o.locationId)
      );
    }
    return list;
  }, [offices, selections.region, search, marketsEnabled]);

  const latest = aggregate.length ? aggregate[aggregate.length - 1] : null;
  const prev   = aggregate.length >= 2 ? aggregate[aggregate.length - 2] : null;

  const loadedCount  = snapshotMonths.size;
  const totalMonths  = allMonths.length;
  const pendingCount = totalMonths - loadedCount;
  const closedCount  = offices.filter((o) => !o.isActive).length;
  const missingLifecycleDates = useMemo(() => offices
    .filter(isLifecycleDateNeeded)
    .sort((a, b) => (a.locationId ?? "").localeCompare(b.locationId ?? "")),
  [offices]);

  // Per-office: build snapshot lookup by locationId × month
  const snapByLocMonth = useMemo(() => {
    const m = new Map();
    for (const office of offices) {
      for (const snap of office.snapshots) {
        const ym = toYYYYMM(snap.snapshotDate);
        if (ym) m.set(`${office.locationId}|${ym}`, snap);
      }
    }
    return m;
  }, [offices]);

  // Keep summary totals on the same source as the visible office matrix. The
  // aggregate's FT/contractor fields can be null when a manual total override
  // exists, even though the other office snapshots still carry valid splits.
  const officeMeasureByMonth = useMemo(() => {
    const totals = new Map();
    for (const month of allMonths) {
      let value = 0;
      let hasValue = false;
      let hasSnapshot = false;
      let partial = false;
      for (const office of offices) {
        const snap = snapByLocMonth.get(`${office.locationId}|${month}`);
        if (!snap) continue;
        hasSnapshot = true;
        if (workerType !== "all" && snap.splitReconciles === false) {
          partial = true;
        }
        const measureValue = getValue(snap, workerType);
        if (measureValue == null) {
          partial = true;
          continue;
        }
        const cell = classifyCell(snap, snapshotMonths.has(month), office, month, workerType);
        if (cell.type === "data" || cell.type === "zero") {
          value += cell.value;
          hasValue = true;
        } else if (measureValue === 0) {
          // Lifecycle-filtered zeroes contribute zero to the office subtotal,
          // just as they do to the aggregate, without becoming visible cells.
          hasValue = true;
        }
      }
      const hasAggregate = aggregateByMonth.has(month);
      totals.set(month, {
        value: hasValue ? value : null,
        partial: partial || (hasAggregate && !hasSnapshot),
      });
    }
    return totals;
  }, [allMonths, offices, snapByLocMonth, snapshotMonths, workerType, aggregateByMonth]);

  /**
   * Get cell info for a given office × month:
   *   { type: "data"|"pending"|"gap"|"zero", value }
   */
  function getCellInfo(office, monthStr) {
    const snap = snapByLocMonth.get(`${office.locationId}|${monthStr}`);
    return classifyCell(snap, snapshotMonths.has(monthStr), office, monthStr, workerType);
  }

  /**
   * Compute MoM delta for an office: latest calendar month minus the prior
   * calendar month (last two entries in allMonths).
   *
   * Returns 0 (displayed as "±0") when either of the two most-recent months
   * is a gap or pending cell — this prevents closed/offline offices from
   * showing stale deltas as though they still changed last month.
   * Returns null only when there are fewer than 2 months in the dataset.
   */
  function getOfficeMoM(office) {
    const n = allMonths.length;
    if (n < 2) return null;
    const latestCell = getCellInfo(office, allMonths[n - 1]);
    const prevCell   = getCellInfo(office, allMonths[n - 2]);
    if (latestCell.type === "gap" || latestCell.type === "pending" ||
        prevCell.type   === "gap" || prevCell.type   === "pending") {
      return 0;
    }
    return (latestCell.value ?? 0) - (prevCell.value ?? 0);
  }

  /**
   * Get sparkline values for an office (non-pending, non-gap values only).
   */
  function getSparkValues(office) {
    return allMonths
      .map((m) => getCellInfo(office, m))
      .filter((c) => c.type === "data" || c.type === "zero")
      .map((c) => c.value);
  }

  // Sort the filtered office rows.
  // When sortCol === "name": sort by Market first (asc/desc), then Alias within market.
  // Other sort keys fall through to numeric compare; tie-break alphabetical by alias.
  const sortedOffices = useMemo(() => {
    const sorted = [...filteredOffices];
    sorted.sort((a, b) => {
      const nameA = (a.alias || a.locationId).toLowerCase();
      const nameB = (b.alias || b.locationId).toLowerCase();

      if (sortCol === "name") {
        const mktA = marketsEnabled ? (a.market || "").toLowerCase() : "";
        const mktB = marketsEnabled ? (b.market || "").toLowerCase() : "";
        const dir  = sortDir === "asc" ? 1 : -1;
        const mktCmp = mktA.localeCompare(mktB) * dir;
        if (mktCmp !== 0) return mktCmp;
        return nameA.localeCompare(nameB) * dir;
      } else if (sortCol === "mom") {
        // Use null as sentinel for "no MoM data" — always sort to the end.
        const momA = getOfficeMoM(a);
        const momB = getOfficeMoM(b);
        if (momA == null && momB == null) return nameA.localeCompare(nameB);
        if (momA == null) return 1;
        if (momB == null) return -1;
        const cmp = (momA - momB) * (sortDir === "asc" ? 1 : -1);
        if (cmp !== 0) return cmp;
        return nameA.localeCompare(nameB);
      } else {
        // Month column: sort by that month's value for each office.
        const cellA = getCellInfo(a, sortCol);
        const cellB = getCellInfo(b, sortCol);
        const valA = (cellA.type === "data" || cellA.type === "zero") ? cellA.value : null;
        const valB = (cellB.type === "data" || cellB.type === "zero") ? cellB.value : null;
        if (valA == null && valB == null) return nameA.localeCompare(nameB);
        if (valA == null) return 1;
        if (valB == null) return -1;
        const cmp = (valA - valB) * (sortDir === "asc" ? 1 : -1);
        if (cmp !== 0) return cmp;
        return nameA.localeCompare(nameB);
      }
    });
    return sorted;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOffices, sortCol, sortDir, snapByLocMonth, snapshotMonths, allMonths, workerType, marketsEnabled]);

  // marketRows: synthetic aggregate rows when granularity === "market".
  // Each row sums monthly values for all offices in that market.
  const marketRows = useMemo(() => {
    if (displayGranularity !== "market" || viewBy !== "office") return [];
    const marketMap = new Map();
    for (const office of sortedOffices) {
      const mktKey = office.market || office.locationId;
      if (!marketMap.has(mktKey)) {
        marketMap.set(mktKey, {
          _isMarketRow: true,
          locationId: `_mkt_${mktKey}`,
          market: mktKey,
          alias: mktKey,
          monthTotals: {},
          officesCount: 0,
        });
      }
      const row = marketMap.get(mktKey);
      row.officesCount += 1;
      for (const m of allMonths) {
        const cell = getCellInfo(office, m);
        if (cell.type === "data" || cell.type === "zero") {
          row.monthTotals[m] = (row.monthTotals[m] ?? 0) + cell.value;
        }
      }
    }
    return [...marketMap.values()];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayGranularity, viewBy, sortedOffices, allMonths, snapByLocMonth, snapshotMonths, workerType]);

  /**
   * Handle a column header click — cycles asc → desc → asc.
   * Clicking a new column always starts ascending for "name", descending for numeric cols.
   */
  function handleHeaderClick(col) {
    if (sortCol === col) {
      setRvs({ dir: sortDir === "asc" ? "desc" : "asc" });
    } else {
      // Numeric columns (month values, MoM) default to desc; name defaults to asc.
      setRvs({ sort: col, dir: col === "name" ? "asc" : "desc" });
    }
  }

  // Portfolio total row values (from aggregate)
  const portfolioValues = useMemo(() =>
    allMonths.map((m) => {
      return officeMeasureByMonth.get(m)?.value ?? null;
    }),
  [allMonths, officeMeasureByMonth]);

  const measureTotalValues = useMemo(() => allMonths.map((month) => {
    const snap = aggregateByMonth.get(month);
    if (!snap) return null;
    const officeMeasure = officeMeasureByMonth.get(month);
    const cell = makeMeasureTotalCell(snap, measure, officeMeasure);
    return cell ? { ...cell, officeValue: officeMeasure?.value ?? null } : null;
  }), [allMonths, aggregateByMonth, officeMeasureByMonth, measure]);

  const portfolioSparkValues = portfolioValues.filter((v) => v != null);
  const portfolioMoM = (() => {
    if (!latest || !prev) return null;
    const current = officeMeasureByMonth.get(toYYYYMM(latest.snapshotDate));
    const previous = officeMeasureByMonth.get(toYYYYMM(prev.snapshotDate));
    if (!current || !previous || current.value == null || previous.value == null
        || current.partial !== previous.partial) return null;
    return current.value - previous.value;
  })();
  const companyMoM = (() => {
    if (!latest || !prev) return { delta: null, remoteBoundary: false };
    const latestIndex = allMonths.indexOf(toYYYYMM(latest.snapshotDate));
    const previousIndex = allMonths.indexOf(toYYYYMM(prev.snapshotDate));
    const currentCell = measureTotalValues[latestIndex];
    const previousCell = measureTotalValues[previousIndex];
    return getComparableMeasureMoM(currentCell, previousCell);
  })();
  const latestMonth = latest ? toYYYYMM(latest.snapshotDate) : null;
  const latestOfficeMeasure = latestMonth ? officeMeasureByMonth.get(latestMonth) : null;
  const latestCompanyTotalCell = latestMonth
    ? measureTotalValues[allMonths.indexOf(latestMonth)]
    : null;

  // Unassigned row (aggregate.unassigned)
  const unassignedValues = useMemo(() =>
    allMonths.map((m) => aggregateByMonth.get(m)?.unassigned ?? null),
  [allMonths, aggregateByMonth]);

  const unassignedSparkValues = unassignedValues.filter((v) => v != null);
  const unassignedMoM = (() => {
    const vals = unassignedValues.filter((v) => v != null);
    if (vals.length < 2) return null;
    return vals[vals.length - 1] - vals[vals.length - 2];
  })();

  // Per-measure distributed (remote) totals used in footer Row B + Row C.
  const unassignedFtValues = useMemo(() =>
    allMonths.map((m) => aggregateByMonth.get(m)?.unassignedFt ?? null),
  [allMonths, aggregateByMonth]);

  const unassignedCwValues = useMemo(() =>
    allMonths.map((m) => aggregateByMonth.get(m)?.unassignedCw ?? null),
  [allMonths, aggregateByMonth]);

  const pendingMonthSet = useMemo(() => {
    const s = new Set();
    for (const m of allMonths) if (!snapshotMonths.has(m)) s.add(m);
    return s;
  }, [allMonths, snapshotMonths]);

  // Latest unassigned value + its month for the 5th KPI tile
  const latestUnassignedInfo = useMemo(() => {
    for (let i = allMonths.length - 1; i >= 0; i--) {
      if (unassignedValues[i] != null) {
        return { value: unassignedValues[i], month: allMonths[i] };
      }
    }
    return null;
  }, [allMonths, unassignedValues]);

  const isFilterActive = search.trim() !== "" ||
    (marketsEnabled && rvs.region.length > 0) ||
    sortCol !== "name" || sortDir !== "asc";

  const isSummaryHidden = search.trim() !== "" || (marketsEnabled && rvs.region.length > 0);

  function handleClear() {
    setRvs({ q: "", region: [], sort: "name", dir: "asc" });
  }

  // Build sort fields for SortControl: base fields + one entry per loaded month.
  // Month fields are numeric (desc first = most recent months show highest on top).
  const sortFields = useMemo(() => {
    const monthFields = allMonths.map((m) => ({
      value:   m,
      label:   fmtMonthLabel(m),
      numeric: true,
    }));
    const baseFields = marketsEnabled
      ? HC_SORT_FIELDS_BASE
      : HC_SORT_FIELDS_BASE.map((field) => field.value === "name" ? { ...field, label: "Office" } : field);
    return [baseFields[0], ...monthFields, baseFields[1]];
  }, [allMonths, marketsEnabled]);

  // SortControl primary/secondary state (secondary is unused; we expose it for
  // structural parity with other SortControl callers but wire only primary).
  const sortControlPrimary   = { key: sortCol, dir: sortDir };
  const sortControlSecondary = { key: null, dir: "asc" };

  function handleSortControlChange({ primary }) {
    setRvs({ sort: primary.key, dir: primary.dir });
  }

  const FILTER_DIMENSIONS = [
    {
      id: "region",
      label: "Region",
      options: regions.map((r) => ({ value: r, label: r })),
    },
  ];

  const isCompact = density === DENSITY_COMPACT;
  const td = isCompact ? TD_COMPACT : TD;

  // Scroll-sync refs for the sticky header / body split on the matrix table.
  // Header table is inside a sticky shell with NO overflow; body table is in
  // an overflowX:auto wrapper; JS syncs scrollLeft on body scroll.
  // Pattern mirrors OfficesPage.jsx headerScrollRef / bodyScrollRef.
  const hcHeaderRef = useRef(null);
  const hcBodyRef   = useRef(null);
  const handleHcBodyScroll = useCallback(() => {
    if (hcHeaderRef.current && hcBodyRef.current) {
      hcHeaderRef.current.scrollLeft = hcBodyRef.current.scrollLeft;
    }
  }, []);
  // Min-width for both colgroup tables:
  // Name(200) + Trend(90 when comfortable) + months(100 each) + MoM(70).
  const hcMinW = 200 + (isCompact ? 0 : 90) + allMonths.length * 100 + 70;

  return (
    <ReportShell
        recordNavigation
        onBack={onBack}
        backTestId="hc-trend-back"
        prevReport={prevReport}
        nextReport={nextReport}
        onNavigateReport={onNavigateReport}
      >

      <div style={{ maxWidth: 1400, padding: "0 32px 48px", margin: "0 auto", width: "100%" }}>

        <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {loading && (
        <div style={{ color: T.mid, fontSize: T.fsBase, padding: "32px 0" }}>Loading…</div>
      )}

      {!loading && !error && (
        <>
          {/* KPI strip — always visible, recounts for the selected Measure. */}
          <div style={{ marginBottom: 18 }}>
            <KpiBand testId="hc-trend-kpi-row" ariaLabel="Report metrics">
              {/* Total company — measure-aware */}
              <KpiTile
                label="Total company"
                value={latestCompanyTotalCell?.value != null
                  ? fmtN(latestCompanyTotalCell.value)
                  : "—"}
                sub={latest
                  ? `${measure === "all" ? "office-assigned + remote · snapshot" : (MEASURE_LABEL[measure] ?? "all workers")} · ${fmtMonthShort(toYYYYMM(latest.snapshotDate))}${latestCompanyTotalCell?.partial ? " · partial — remote or office split unavailable" : ""}`
                  : undefined}
                tooltip={latestCompanyTotalCell?.partial
                  ? "Partial total: one or more office worker splits or the selected measure's remote split is unavailable."
                  : undefined}
                height={KPI_TILE_ROW_HEIGHT}
                testId="hc-kpi-total-company"
              />
              {/* In offices — market-dependent */}
              {officesEnabled && <KpiTile
                label="In offices"
                value={latestOfficeMeasure?.value != null
                  ? fmtN(latestOfficeMeasure.value)
                  : "—"}
                sub={latest
                  ? `assigned · ${fmtMonthShort(toYYYYMM(latest.snapshotDate))}${latestOfficeMeasure?.partial ? " · partial — office split unavailable" : ""}`
                  : undefined}
                tooltip={latestOfficeMeasure?.partial
                  ? "Partial office total: one or more office snapshots do not include this worker-type split."
                  : "Covers all offices including vacated and retired — any office with a headcount snapshot for the selected month is included."}
                height={KPI_TILE_ROW_HEIGHT}
                testId="hc-kpi-in-offices"
              />}
              {/* Distributed / remote — shows real ft/cw when the import carries a breakdown */}
              <KpiTile
                label="Distributed / remote"
                value={(() => {
                  if (measure === "all")  return latestUnassignedInfo != null ? fmtN(latestUnassignedInfo.value) : "—";
                  if (measure === "ft")   return latest?.unassignedFt != null ? fmtN(latest.unassignedFt) : "—";
                  /* contractor */        return latest?.unassignedCw != null ? fmtN(latest.unassignedCw) : "—";
                })()}
                sub={(() => {
                  if (measure === "all" && latestUnassignedInfo)              return `no office assigned · ${fmtMonthShort(latestUnassignedInfo.month)}`;
                  if (measure === "ft"   && latest?.unassignedFt != null)     return `full-time remote · ${fmtMonthShort(toYYYYMM(latest.snapshotDate))}`;
                  if (measure === "contractor" && latest?.unassignedCw != null) return `contractors remote · ${fmtMonthShort(toYYYYMM(latest.snapshotDate))}`;
                  return "breakdown not in import";
                })()}
                height={KPI_TILE_ROW_HEIGHT}
                testId="hc-kpi-unassigned"
              />
              {/* Net change MoM — measure-aware */}
              {(() => {
                const subLabel = (() => {
                  if (!prev || !latest) return undefined;
                  const range = `${fmtMonthShort(toYYYYMM(prev.snapshotDate))}→${fmtMonthShort(toYYYYMM(latest.snapshotDate))}`;
                  return companyMoM.remoteBoundary ? `in-office · ${range}` : range;
                })();

                return (
                  <KpiTile
                    label="Net change MoM"
                     value={(() => {
                       if (companyMoM.delta == null) return "—";
                       const d = companyMoM.delta;
                       return <span style={{ color: T.mid }}><span aria-hidden="true" style={{ fontSize: T.fsXs, opacity: 0.6, marginRight: 4 }}>{d > 0 ? "▲" : d < 0 ? "▼" : ""}</span>{fmtDelta(d)}</span>;
                    })()}
                    tooltip={companyMoM.remoteBoundary
                      ? "Remote split availability changed between snapshots — showing the in-office change for comparability."
                      : undefined}
                     color={T.mid}
                    sub={subLabel}
                    height={KPI_TILE_ROW_HEIGHT}
                    testId="hc-kpi-net-change"
                  />
                );
              })()}
              {/* Months loaded — unchanged */}
              <KpiTile
                label="Months loaded"
                value={String(loadedCount)}
                sub={pendingCount > 0 ? `of ${totalMonths} (${pendingCount} pending)` : `of ${totalMonths}`}
                height={KPI_TILE_ROW_HEIGHT}
                testId="hc-kpi-months"
              />
            </KpiBand>
          </div>
          {latest?.snapshotDate && (
            <div data-testid="hc-period-label" style={{ color: T.mid, fontSize: T.fsSm, margin: "-8px 0 14px" }}>
              Latest headcount snapshot: {fmtSnapshotDate(latest.snapshotDate)}
            </div>
          )}

          {/* ── Toolbar — rows ───────────────────────────────────────────── */}
          <div style={{ marginBottom: 18 }}>

            {/* Row 1: Group by [left, office table only] · View by · MonthDropdown … Measure · Display [right] */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              {/* Group by — appears to the LEFT of View by, visible in office table mode only */}
              {viewBy === "office" && display === "table" && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: T.fsXxs, color: T.light, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 2 }}>
                    Group by:
                  </span>
                  <ToolbarViewToggle
                    segments={[
                      { key: "office", label: "Office", testid: "hc-granularity-office" },
                      { key: "market", label: "Market", testid: "hc-granularity-market" },
                    ]}
                    value={granularity}
                    onChange={setGranularity}
                  />
                </div>
              )}
              <span style={{ fontSize: T.fsXxs, color: T.light, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 2 }}>
                View by:
              </span>
              <ToolbarViewToggle
                segments={[
                  ...(officesEnabled ? [{ key: "office", label: "Office", testid: "hc-viewby-office" }] : []),
                  { key: "division",  label: "Division",  testid: "hc-viewby-division"  },
                  { key: "acquired",  label: "M&A",       testid: "hc-viewby-acquired"  },
                  ...(marketsEnabled ? [{ key: "geography", label: "Geography", testid: "hc-viewby-geography" }] : []),
                ]}
                value={viewBy}
                onChange={(v) => setRvs({ viewBy: v })}
              />
              {/* Month dropdown — visible for non-office views when multiple months exist */}
              {viewBy !== "office" && display === "table" && (
                <MonthDropdown
                  snapMonths={snapshotMonths}
                  value={selectedMonth}
                  onChange={setSelectedMonth}
                />
              )}
              {/* Right: Measure · Display */}
              <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                {/* Measure */}
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: T.fsXxs, color: T.light, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 2 }}>
                    Measure:
                  </span>
                  <ToolbarViewToggle
                    segments={[
                      { key: "all",        label: "All workers", testid: "hc-measure-all"        },
                      { key: "ft",         label: "Full-time",   testid: "hc-measure-ft"         },
                      { key: "contractor", label: "Contractor",  testid: "hc-measure-contractor" },
                    ]}
                    value={measure}
                    onChange={(v) => setRvs({ measure: v })}
                  />
                </div>
                {/* Display */}
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: T.fsXxs, color: T.light, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 2 }}>
                    Display:
                  </span>
                  <ToolbarViewToggle
                    segments={[
                      { key: "table", label: "Table", testid: "hc-display-table" },
                      { key: "chart", label: "Chart", testid: "hc-display-chart" },
                    ]}
                    value={display}
                    onChange={(v) => setRvs({ view: v })}
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Left (Search · Filter · Sort · Clear) + Right (Density) */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minHeight: T.toolbarRowH }}>
              {officesEnabled && <AlertChip
                testId="hc-lifecycle-alert-chip"
                storageKey="ripple.headcountTrend.lifecycleAlertChip.open"
                chipLabel="lifecycle dates"
                panelHeader={`${missingLifecycleDates.length} offices need a lifecycle date`}
                messages={missingLifecycleDates.map((office) => ({
                  id: office.locationId,
                  severity: "warn",
                  text: (
                    <>
                      {office.locationId}
                      <span style={{ color: T.mid }}> · {office.alias || "Unnamed office"}</span>
                    </>
                  ),
                  actionLabel: "Add date ->",
                  onAction: () => onOpenLocation?.(office.locationId),
                }))}
                onDismissAll={() => {}}
              />}

              {/* Office view: Search · Filter · Sort · Clear, plus Market|Office sub-toggle */}
              {viewBy === "office" && (
                <>
                  {display === "table" && (
                    <SearchInput
                      value={search}
                      onChange={(v) => setRvs({ q: v })}
                      placeholder="Search this report"
                      testId="hc-search"
                    />
                  )}
                  {marketsEnabled && <ConsolidatedFilter
                    dimensions={FILTER_DIMENSIONS}
                    selections={selections}
                    onChange={(newSel) => setRvs({ region: newSel.region ?? [] })}
                    testId="hc-filter-btn"
                  />}
                  {display === "table" && (
                    <SortControl
                      fields={sortFields}
                      primary={sortControlPrimary}
                      secondary={sortControlSecondary}
                      onChange={handleSortControlChange}
                      testId="hc-sort"
                      defaultKey="name"
                    />
                  )}
                  {isFilterActive && (
                    <button
                      type="button"
                      data-testid="hc-clear-btn"
                      onClick={handleClear}
                      onMouseEnter={(e) => { e.currentTarget.style.background = T.blueSoft; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      style={{
                        background: "transparent",
                        border: "1px solid transparent",
                        borderRadius: 8,
                        color: T.blue,
                        fontSize: T.fsBase,
                        fontWeight: T.fwBold,
                        cursor: "pointer",
                        padding: "7px 11px",
                      }}
                    >
                      Clear
                    </button>
                  )}
                </>
              )}

              {/* Non-office views: Search · Sort (by name or value) */}
              {viewBy !== "office" && display === "table" && (
                <>
                  <SearchInput
                    value={dimSearch}
                    onChange={setDimSearch}
                    placeholder="Search this report"
                    testId="hc-dim-search"
                  />
                  <SortControl
                    fields={[
                      { value: "value", label: "Count", numeric: true },
                      { value: "name",  label: "Name",  numeric: false },
                    ]}
                    primary={{ key: dimSortField, dir: dimSortDir }}
                    secondary={{ key: null, dir: "asc" }}
                    onChange={({ primary }) => { setDimSortField(primary.key); setDimSortDir(primary.dir); }}
                    testId="hc-dim-sort"
                    defaultKey="value"
                  />
                </>
              )}

              {/* Right: Density (table only) */}
              <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                {/* Density — visible in office table view only */}
                {display === "table" && viewBy === "office" && (
                  <CompactDensitySwitch
                    checked={density === DENSITY_COMPACT}
                    onChange={() => setRvs({ density: density === DENSITY_COMFORTABLE ? DENSITY_COMPACT : DENSITY_COMFORTABLE })}
                    testId="hc-density-toggle"
                  />
                )}
              </div>
            </div>
          </div>
          {/* ── End toolbar ──────────────────────────────────────────────── */}

          {/* ── Chart: office — two-panel (area-fill line + biggest movers) ── */}
          {display === "chart" && viewBy === "office" && (
            aggregate.length === 0 ? (
              <div style={{ color: T.mid, fontSize: T.fsBase, padding: "32px 0" }}>
                No headcount imports found. Apply a Workday headcount import to populate this report.
              </div>
            ) : (
              <div style={{
                background: T.card,
                border: `1px solid ${T.border}`,
                borderRadius: 12,
                padding: "18px 20px",
                marginBottom: 18,
              }}>
                <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, margin: "0 0 2px" }}>
                  Portfolio headcount over time
                </div>
                <div style={{ fontSize: T.fsXs, color: T.light, margin: "0 0 12px" }}>
                  Assigned (solid) · Total company incl. remote (dashed).
                </div>
                <OfficeChartView
                  allMonths={allMonths}
                  snapshotMonths={snapshotMonths}
                  workerType={workerType}
                  offices={filteredOffices}
                  snapByLocMonth={snapByLocMonth}
                  officeMeasureByMonth={officeMeasureByMonth}
                  measureTotalValues={measureTotalValues}
                  pendingCount={pendingCount}
                />
              </div>
            )
          )}

          {/* ── Chart: dimension breakdowns ─────────────────────────────── */}
          {display === "chart" && viewBy !== "office" && (
            <div style={{
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              padding: "18px 20px",
              marginBottom: 18,
            }}>
              <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, margin: "0 0 2px" }}>
                {viewBy === "division"  ? "Headcount by division over time"         :
                 viewBy === "acquired"  ? "Headcount by M&A over time" :
                                          "Headcount by geography over time"}
              </div>
              <div style={{ fontSize: T.fsXs, color: T.light, margin: "0 0 12px" }}>
                Top {DIMENSION_CHART_CAP} groups by latest count.
              </div>
              {viewBy === "division" && (
                <DimensionTrendChart
                  rollupRows={data?.divisionRollup ?? []}
                  labelKey="division"
                  allMonths={allMonths}
                  snapshotMonths={snapshotMonths}
                  measure={measure}
                />
              )}
              {viewBy === "acquired" && (
                <DimensionTrendChart
                  rollupRows={data?.acquiredCompanyRollup ?? []}
                  labelKey="cohortLabel"
                  allMonths={allMonths}
                  snapshotMonths={snapshotMonths}
                  measure={measure}
                />
              )}
              {viewBy === "geography" && (
                <DimensionTrendChart
                  rollupRows={data?.distributedRollup ?? []}
                  labelKey="geoLabel"
                  allMonths={allMonths}
                  snapshotMonths={snapshotMonths}
                  measure={measure}
                />
              )}
            </div>
          )}

          {/* ── Table view: division ───────────────────────────────────── */}
          {display === "table" && viewBy === "division" && (
            <DivisionPanel
              divisionRollup={data?.divisionRollup ?? []}
              groupRollup={data?.groupRollup ?? []}
              latestSnapshotDate={latest?.snapshotDate ?? null}
              selectedMonth={selectedMonth}
              workerType={workerType}
              searchQuery={dimSearch}
              sortField={dimSortField}
              sortDir={dimSortDir}
            />
          )}

          {/* ── Table view: acquired company ───────────────────────────── */}
          {display === "table" && viewBy === "acquired" && (
            <AcquiredCompanyPanel
              acquiredCompanyRollup={data?.acquiredCompanyRollup ?? []}
              latestSnapshotDate={latest?.snapshotDate ?? null}
              selectedMonth={selectedMonth}
              workerType={workerType}
              searchQuery={dimSearch}
              sortField={dimSortField}
              sortDir={dimSortDir}
            />
          )}

          {/* ── Table view: geography ─────────────────────────────────── */}
          {display === "table" && viewBy === "geography" && (() => {
                const distRollup = data?.distributedRollup ?? [];
                const snapMonthsSorted = [...snapshotMonths].sort();
                const effectiveGeoMonth = selectedMonth && snapshotMonths.has(selectedMonth)
                  ? selectedMonth
                  : (snapMonthsSorted[snapMonthsSorted.length - 1] ?? null);
                const hasDistData = distRollup.length > 0;
                if (!hasDistData) {
                  return (
                    <div style={{
                      background: T.card,
                      border: `1px solid ${T.border}`,
                      borderRadius: 12,
                      padding: "36px 24px",
                      textAlign: "center",
                      marginBottom: 18,
                    }}>
                      <div style={{ fontSize: T.fsLg, marginBottom: 8 }}>🗺️</div>
                      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
                        No data yet
                      </div>
                      <div style={{ fontSize: T.fsBase, color: T.mid }}>
                        Re-ingest headcount files to populate this view.
                      </div>
                    </div>
                  );
                }
                return marketsEnabled ? (
                  <DistributedPanel
                    distributedRollup={distRollup}
                    offices={offices}
                    latestSnapshotDate={latest?.snapshotDate ?? null}
                    selectedMonth={effectiveGeoMonth}
                    workerType={workerType}
                    searchQuery={dimSearch}
                    sortField={dimSortField}
                    sortDir={dimSortDir}
                  />
                ) : null;
              })()}

          {/* ── Table view: office matrix ─────────────────────────────── */}
          {display === "table" && viewBy === "office" && (aggregate.length === 0 ? (
                <div style={{ color: T.mid, fontSize: T.fsBase, padding: "32px 0" }}>
                  No headcount imports found. Apply a Workday headcount import to populate this report.
                </div>
              ) : (
            <div style={{
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              marginBottom: 18,
            }}>
              {/* Sticky header shell — NO overflow on this div.
                   An overflow ancestor creates a scroll container, which confines
                   position:sticky to pin within that container instead of the page
                   (CSS §9.7 scroll container creation).
                   The inner clip div uses overflowX:hidden which clips horizontally
                   without becoming a scroll container. Pattern: OfficesPage.jsx.
                   Background + top-radius ensure no white strip or exposed card
                   corner when the header pins at the viewport top. */}
              <div style={{ position: "sticky", top: 0, zIndex: 4, background: T.lightGray, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                <div ref={hcHeaderRef} style={{ overflowX: "hidden", borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                  <OverflowTable overflowScrollRef={hcBodyRef}
                    data-testid="hc-trend-header"
                    style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5, tableLayout: "fixed", minWidth: hcMinW }}
                  >
                    <colgroup>
                      <col style={{ width: 200 }} />
                      {!isCompact && <col style={{ width: 90 }} />}
                      {allMonths.map((m) => <col key={m} style={{ width: 100 }} />)}
                      <col style={{ width: 70 }} />
                    </colgroup>
                <thead>
                  <tr>
                    {/* Office/Market column header — label changes with granularity.
                        Root cause of Trend sparkline gap: without an explicit width this
                        column was flex-growing to absorb all slack in a 100%-wide table,
                        pushing the Trend cell far from the office label. A fixed width
                        (width: 200) makes it content-hugging; leftover width flows to
                        month columns instead. */}
                    <th style={{ ...TH, textAlign: "left", width: 200, minWidth: 160, position: "sticky", left: 0, background: T.lightGray, zIndex: 4, padding: 0 }}>
                      <button
                        type="button"
                        data-testid="hc-sort-col-name"
                        data-sort-dir={sortCol === "name" ? sortDir : undefined}
                        onClick={() => handleHeaderClick("name")}
                        style={sortHeaderBtnStyle}
                      >
                        {displayGranularity === "market" ? "Market" : "Office"}
                        <TableSortCaret active={sortCol === "name"} dir={sortDir === "asc" ? 1 : -1} />
                      </button>
                    </th>

                    {/* Trend/sparkline column — hidden in compact mode */}
                    {!isCompact && (
                      <th style={{ ...TH, textAlign: "center", width: 90 }} data-testid="hc-col-trend">Trend</th>
                    )}

                    {/* Month columns */}
                    {allMonths.map((m) => {
                      const isCurrent = m === currentMonth && currentMonth != null;
                      return (
                        <th
                          key={m}
                          style={{
                            ...TH,
                            textAlign: "right",
                            whiteSpace: "nowrap",
                            padding: 0,
                            ...(isCurrent ? { background: T.hcCurrentHeader, borderBottom: `2px solid ${T.blue}` } : {}),
                          }}
                        >
                          <button
                            type="button"
                            data-testid={`hc-sort-col-${m}`}
                            data-sort-dir={sortCol === m ? sortDir : undefined}
                            onClick={() => handleHeaderClick(m)}
                            style={{
                              ...sortHeaderBtnStyle,
                              justifyContent: "flex-end",
                              color: isCurrent ? T.blue : (pendingMonthSet.has(m) ? T.borderDark : (sortCol === m ? T.blue : "var(--v2-mid)")),
                            }}
                          >
                            {fmtMonthShort(m)}
                            <TableSortCaret active={sortCol === m} dir={sortDir === "asc" ? 1 : -1} />
                          </button>
                        </th>
                      );
                    })}

                    {/* MoM column */}
                    <th style={{ ...TH, textAlign: "right", padding: 0 }} title="Month-over-month change - latest month vs the prior month.">
                      <button
                        type="button"
                        data-testid="hc-sort-col-mom"
                        data-sort-dir={sortCol === "mom" ? sortDir : undefined}
                        onClick={() => handleHeaderClick("mom")}
                        style={{ ...sortHeaderBtnStyle, justifyContent: "flex-end" }}
                      >
                        MoM
                        <TableSortCaret active={sortCol === "mom"} dir={sortDir === "asc" ? 1 : -1} />
                      </button>
                    </th>
                  </tr>
                </thead>
                  </OverflowTable>
                  {/* Company summary — pinned below column headers; hidden when search/filter is active */}
                  {!isSummaryHidden && (
                    <table
                      data-testid="hc-trend-summary"
                      style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5, tableLayout: "fixed", minWidth: hcMinW, borderBottom: `2px solid ${T.border}` }}
                    >
                      <colgroup>
                        <col style={{ width: 200 }} />
                        {!isCompact && <col style={{ width: 90 }} />}
                        {allMonths.map((m) => <col key={m} style={{ width: 100 }} />)}
                        <col style={{ width: 70 }} />
                      </colgroup>
                      <tbody>
                        {/* Row C: = Total company */}
                        {(() => {
                          const totalValues = measureTotalValues;
                          const totalSparkValues = totalValues.filter((v) => v != null).map((v) => v.value);
                          return (
                            <tr style={{ background: T.rowActive }}>
                              <td style={{ ...td, fontWeight: T.fwBold, position: "sticky", left: 0, background: T.rowActive, zIndex: 1, color: T.blue }}>
                                <span style={{ color: T.light, marginRight: 4 }}>=</span>
                                Total company
                              </td>
                              {!isCompact && (
                                <td style={{ ...td, textAlign: "center" }}>
                                  <div style={{ display: "flex", justifyContent: "center" }}>
                                    <Sparkline values={totalSparkValues} />
                                  </div>
                                </td>
                              )}
                              {totalValues.map((cell, i) => {
                                const m = allMonths[i];
                                if (cell == null) return <td key={m} style={{ ...td, ...PENDING_CELL_STYLE }}>—</td>;
                                if (cell.partial) {
                                  return (
                                    <td
                                      key={m}
                                      style={{ ...td, textAlign: "right", fontWeight: T.fwBold, color: T.blue }}
                                      title={cell.remoteAvailable
                                        ? "Partial office total · one or more office worker-type splits are unavailable"
                                        : "Office-assigned only · remote split not available for this month"}
                                    >
                                      <span style={{ color: T.light }}>~</span>{fmtN(cell.value)}
                                    </td>
                                  );
                                }
                                return <td key={m} style={{ ...td, textAlign: "right", fontWeight: T.fwBold, color: T.blue }}>{fmtN(cell.value)}</td>;
                              })}
                              <td
                                style={{ ...td, textAlign: "right", fontWeight: T.fwBold }}
                                title={companyMoM.remoteBoundary
                                  ? "Remote split availability changed — showing in-office change for comparability"
                                  : undefined}
                              >
                                 <DirectionDelta value={companyMoM.delta} />
                              </td>
                            </tr>
                          );
                        })()}

                        {/* Row A: In office (assigned) */}
                        <tr style={{ background: T.planRowBg }}>
                          <td style={{ ...td, fontWeight: T.fwBold, position: "sticky", left: 0, background: T.planRowBg, zIndex: 1 }}>
                            In office{" "}
                            <span style={{ fontWeight: "normal", color: T.light }}>(assigned)</span>
                          </td>
                          {!isCompact && (
                            <td style={{ ...td, textAlign: "center" }}>
                              <div style={{ display: "flex", justifyContent: "center" }}>
                                <Sparkline values={portfolioSparkValues} />
                              </div>
                            </td>
                          )}
                          {allMonths.map((m, i) => {
                            const v = portfolioValues[i];
                            const officeMeasure = officeMeasureByMonth.get(m);
                            if (v == null) return <td key={m} style={{ ...td, ...PENDING_CELL_STYLE }}>—</td>;
                            return (
                              <td
                                key={m}
                                style={{ ...td, textAlign: "right", fontWeight: T.fwBold }}
                                title={officeMeasure?.partial
                                  ? "Partial office total · one or more office snapshots do not include this worker-type split"
                                  : undefined}
                              >
                                {officeMeasure?.partial && <span style={{ color: T.light }}>~</span>}
                                {fmtN(v)}
                              </td>
                            );
                          })}
                          <td style={{ ...td, textAlign: "right", fontWeight: T.fwBold }}>
                             <DirectionDelta value={portfolioMoM} />
                          </td>
                        </tr>

                        {/* Row B: + Distributed / remote — real ft/cw when breakdown is available */}
                        {(() => {
                          const distVals     = measure === "all" ? unassignedValues
                            : measure === "ft" ? unassignedFtValues : unassignedCwValues;
                          const distSpark    = distVals.filter((v) => v != null);
                          // Guard: distSpark.length >= 2 is intentional — when only one month of
                          // remote data exists there is no prior month to compare against, so MoM
                          // is undefined. Do NOT relax this to >= 1; a single data point must show "—".
                          const distMoM      = distSpark.length >= 2
                            ? distSpark[distSpark.length - 1] - distSpark[distSpark.length - 2]
                            : null;
                          const hasBreakdown = distSpark.length > 0;
                          const subLabel = measure === "all" ? "no office assigned"
                            : hasBreakdown
                              ? (measure === "ft" ? "full-time remote" : "contractors remote")
                              : "breakdown not in import";
                          return (
                            <tr style={{ background: T.planRowBg, color: T.mid }}>
                              <td style={{ ...td, position: "sticky", left: 0, background: T.planRowBg, zIndex: 1 }}>
                                <span style={{ color: T.light, marginRight: 4 }}>+</span>
                                <span style={{ fontWeight: T.fwSemibold }}>Distributed / remote</span>
                                {!isCompact && (
                                  <div style={{ fontSize: T.fsXxs, color: T.light, marginTop: 1 }}>{subLabel}</div>
                                )}
                              </td>
                              {!isCompact && (
                                <td style={{ ...td, textAlign: "center" }}>
                                  {distSpark.length > 0 && (
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                      <Sparkline values={distSpark} />
                                    </div>
                                  )}
                                </td>
                              )}
                              {allMonths.map((m, i) => {
                                const v = distVals[i];
                                if (v == null) return <td key={m} style={{ ...td, textAlign: "right", color: T.borderDark }}>—</td>;
                                return <td key={m} style={{ ...td, textAlign: "right" }}>{fmtN(v)}</td>;
                              })}
                              <td style={{ ...td, textAlign: "right" }}>
                                {distSpark.length >= 2 ? (
                                  <DirectionDelta value={distMoM} />
                                ) : (
                                  <span style={{ fontSize: T.fsXs, color: T.borderDark }}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              {/* "In office, by office" caption divider — separates summary from per-office rows */}
              {!isSummaryHidden && (
                <div style={{ padding: "4px 12px", fontSize: T.fsXxs, color: T.light, background: T.lightGray, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: T.fwSemibold }}>
                  In office, by office ↓
                </div>
              )}
              {/* Body scroll wrapper — overflowX:auto enables horizontal scroll;
                   onScroll handler syncs the header clip div's scrollLeft. */}
              <div ref={hcBodyRef} onScroll={handleHcBodyScroll} style={{ overflowX: "auto", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
                <table
                  data-testid="hc-trend-matrix"
                  style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5, tableLayout: "fixed", minWidth: hcMinW }}
                >
                  <colgroup>
                    <col style={{ width: 200 }} />
                    {!isCompact && <col style={{ width: 90 }} />}
                    {allMonths.map((m) => <col key={m} style={{ width: 100 }} />)}
                    <col style={{ width: 70 }} />
                  </colgroup>
                <tbody>
                  {/* Market-aggregate rows — rendered when granularity === "market" */}
                  {displayGranularity === "market" && marketRows.map((mkt) => {
                    const monthValues = allMonths.map((m) => mkt.monthTotals[m] ?? null);
                    const validVals = monthValues.filter((v) => v != null);
                    const mktMoM = validVals.length >= 2
                      ? validVals[validVals.length - 1] - validVals[validVals.length - 2]
                      : null;
                    return (
                      <tr
                        key={mkt.locationId}
                        data-testid={`hc-market-row-${mkt.market}`}
                        onMouseEnter={(e) => (e.currentTarget.style.background = T.hoverBg)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ ...td, position: "sticky", left: 0, background: "inherit", zIndex: 1 }}>
                          <div style={{ fontWeight: T.fwSemibold }}>
                            {mkt.alias}
                            {isCompact && (
                              <span style={{ fontSize: T.fsXxs, color: T.light, marginLeft: 6 }}>
                                {mkt.officesCount} {mkt.officesCount === 1 ? "office" : "offices"}
                              </span>
                            )}
                          </div>
                          {!isCompact && (
                            <div style={{ fontSize: T.fsXxs, color: T.light, marginTop: 1 }}>{mkt.officesCount} {mkt.officesCount === 1 ? "office" : "offices"}</div>
                          )}
                        </td>
                        {!isCompact && (
                          <td style={{ ...td, textAlign: "center", width: 90 }}>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                              <Sparkline values={validVals} />
                            </div>
                          </td>
                        )}
                        {allMonths.map((m, i) => {
                          const isCurrent = m === currentMonth && currentMonth != null;
                          return (
                            <td key={m} style={{ ...td, textAlign: "right", ...(isCurrent ? { background: T.hcCurrentCell, fontWeight: T.fwBold } : {}) }}>
                              {monthValues[i] != null ? fmtN(monthValues[i]) : "—"}
                            </td>
                          );
                        })}
                        <td style={{ ...td, textAlign: "right" }}>
                          <DirectionDelta value={mktMoM} />
                        </td>
                      </tr>
                    );
                  })}

                  {/* Per-office rows — rendered when granularity === "office" */}
                  {granularity === "office" && sortedOffices.map((office) => {
                    const tag = stageTag(office.stage);
                    const sparkVals = getSparkValues(office);
                    const mom = getOfficeMoM(office);
                    const cells = allMonths.map((m) => ({ m, cell: getCellInfo(office, m) }));
                    const materialChanges = new Map();
                    for (let i = 1; i < cells.length; i += 1) {
                      const previous = cells[i - 1].cell;
                      const current = cells[i].cell;
                      if (
                        (previous.type === "data" || previous.type === "zero")
                        && (current.type === "data" || current.type === "zero")
                      ) {
                        materialChanges.set(
                          cells[i].m,
                          classifyMaterialHeadcountChange(previous.value, current.value),
                        );
                      }
                    }
                    const latestMonth = allMonths[allMonths.length - 1];
                    const latestChange = materialChanges.get(latestMonth)
                      ?? { isMaterial: false, kind: null, delta: null };
                    return (
                      <tr
                        key={office.locationId}
                        data-testid={`hc-matrix-row-${office.locationId}`}
                        onMouseEnter={(e) => (e.currentTarget.style.background = T.hoverBg)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ ...td, position: "sticky", left: 0, background: "inherit", zIndex: 1 }}>
                          <div style={{ fontWeight: T.fwSemibold }}>
                            {marketsEnabled && office.market && office.alias
                              ? `${office.market} · ${office.alias}`
                              : office.alias || (marketsEnabled ? office.market : null) || office.locationId}
                            {tag && <StatusTag label={tag} />}
                          </div>
                          {/* location-ID subtext — hidden in compact mode */}
                          {!isCompact && (
                            <div
                              data-testid={`hc-loc-id-${office.locationId}`}
                              style={{ fontSize: T.fsXxs, color: T.light, marginTop: 1 }}
                            >
                              {office.locationId}
                            </div>
                          )}
                        </td>
                        {/* Sparkline cell — hidden in compact mode */}
                        {!isCompact && (
                          <td style={{ ...td, textAlign: "center", width: 90 }}>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                              <Sparkline values={sparkVals} />
                            </div>
                          </td>
                        )}
                        {allMonths.map((m) => {
                          const cell = getCellInfo(office, m);
                          const isCurrent = m === currentMonth && currentMonth != null;
                          const lifecycleTitle = lifecycleWarningTitle(cell.lifecycleWarning, office);
                          const hasLifecycleWarning = Boolean(lifecycleTitle);
                          if (cell.type === "pending") {
                            return <td key={m} style={{ ...td, ...PENDING_CELL_STYLE }}>—</td>;
                          }
                          if (cell.type === "gap") {
                            return <td key={m} style={{ ...td, textAlign: "right", color: T.borderDark }}>—</td>;
                          }
                          if (cell.type === "zero") {
                            if (hasLifecycleWarning) {
                              return (
                                <td key={m} style={{ ...td, textAlign: "right", ...(isCurrent ? { background: T.hcCurrentCell } : {}) }}>
                                  <span
                                    style={{ color: T.amber, fontWeight: T.fwBold }}
                                    title={lifecycleTitle}
                                  >
                                    0 ⚠
                                  </span>
                                </td>
                              );
                            }
                            return (
                              <td key={m} style={{ ...td, textAlign: "right", ...(isCurrent ? { background: T.hcCurrentCell } : {}) }}>
                                <span style={{ color: T.mid }}>0</span>
                              </td>
                            );
                          }
                           // Only contradictory source/lifecycle data receives an alarm.
                           if (hasLifecycleWarning) {
                            return (
                              <td key={m} style={{ ...td, textAlign: "right", ...(isCurrent ? { background: T.hcCurrentCell } : {}) }}>
                                <span
                                  style={{ color: T.amber, fontWeight: T.fwBold }}
                                   title={lifecycleTitle}
                                >
                                  {fmtN(cell.value)} ⚠
                                </span>
                              </td>
                            );
                          }
                          return (
                            <td key={m} style={{ ...td, textAlign: "right", ...(isCurrent ? { background: T.hcCurrentCell, fontWeight: T.fwBold } : {}) }}>
                              {fmtN(cell.value)}
                            </td>
                          );
                        })}
                        <td style={{ ...td, textAlign: "right" }}>
                          <DirectionDelta value={mom} emphasize={latestChange.isMaterial} />
                        </td>
                      </tr>
                    );
                  })}

                </tbody>
              </table>
              </div>
            </div>
          ))}
        </>
      )}
      </div>
    </ReportShell>
  );
}

// ─── Shared cell styles ───────────────────────────────────────────────────────

const TH = {
  padding:       "10px 14px",
  fontSize:      T.fsXxs,
  letterSpacing: ".07em",
  textTransform: "uppercase",
  color:         "var(--v2-mid)",
  fontWeight:    T.fwBold,
  borderBottom:  "1px solid var(--v2-line)",
  whiteSpace:    "nowrap",
  position:      "sticky",
  top:           0,
  zIndex:        3,
  background:    "var(--v2-tint)",
};

const TD = {
  padding: "11px 12px",
  borderBottom: `1px solid ${T.bg}`,
  color: T.dark,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const TD_COMPACT = {
  ...TD,
  padding: "5px 12px",
};

const PENDING_CELL_STYLE = {
  background: T.hoverBg,
  color: T.borderDark,
  textAlign: "right",
};

// Sortable column header button — inherits TH visual style but is interactive.
const sortHeaderBtnStyle = {
  display:        "inline-flex",
  alignItems:     "center",
  gap:            0,
  width:          "100%",
  padding:        "10px 14px",
  background:     "none",
  border:         "none",
  cursor:         "pointer",
  fontSize:       T.fsXxs,
  letterSpacing:  ".07em",
  textTransform:  "uppercase",
  color:          "var(--v2-mid)",
  fontWeight:     T.fwBold,
  whiteSpace:     "nowrap",
  textAlign:      "left",
};
